<!-- src/layout/WeightQuery.vue - 账号权重查询页面 -->
<template>
  <div :class="['weight-query', `theme-${currentTheme}`]">
    <!-- 顶部工具栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <h2 class="page-title">账号权重查询</h2>
      </div>
    </div>

    <!-- 输入区域 -->
    <div class="input-section" :class="{'mobile-input': isMobile}">
      <input
        v-model="inputValue"
        class="input-field"
        :placeholder="isMobile ? '输入链接或sec_uid' : '输入作品链接/主页分享链接/sec_uid'"
        type="text"
        @keyup.enter="queryWeight"
      >
      <Button
        @click="queryWeight"
        type="primary"
        :icon="isMobile ? '' : 'ios-search'"
        :loading="isQuerying"
        :long="isMobile"
      >
        {{ isMobile ? '查询' : '查询权重' }}
      </Button>
    </div>

    <!-- VIP/点数信息 -->
    <div class="balance-info" :class="{'mobile-balance': isMobile}">
      <div v-if="vipStatus.is_vip" class="vip-info">
        <Icon type="ios-star" class="vip-icon" />
        <span>VIP到期时间：{{ vipStatus.end_date }}</span>
        <span v-if="vipStatus.days_left" class="days-left">(剩余{{ vipStatus.days_left }}天)</span>
      </div>
      <div v-else class="points-info">
        <span>当前点数：{{ queryBalance }}</span>
        <span v-if="remainingQueries !== undefined" class="remaining-queries">（剩余{{ remainingQueries }}次查询）</span>
        <Button size="small" type="primary" @click="recharge">充值点数</Button>
      </div>
      <div v-if="queryCost > 0" class="query-cost">
        <Icon type="ios-information-circle" />
        <span>本次消耗：{{ queryCost }} 点数</span>
      </div>
    </div>

    <!-- 查询结果 -->
    <div v-if="weightResult" class="weight-result">
      <!-- 账号基本信息 -->
      <div class="account-info">
        <Avatar :src="weightResult.avatarUrl || defaultAvatar" size="large" />
        <div class="user-details">
          <h3>{{ weightResult.nickname || '未知用户' }}</h3>
          <p class="account-id">号：{{ weightResult.account || '无' }}</p>
          <p class="description">{{ weightResult.desc || '暂无简介' }}</p>
        </div>
      </div>

      <!-- 核心指标卡片 -->
      <div class="core-metrics">
        <div class="metric-card">
          <div class="metric-value">{{ coreMetrics.weightScore || '-' }}</div>
          <div class="metric-label">权重分</div>
        </div>
        <div class="metric-card">
          <div class="metric-value">{{ coreMetrics.activity || '-' }}</div>
          <div class="metric-label">活跃度</div>
        </div>
        <div class="metric-card">
          <div class="metric-value">{{ coreMetrics.health || '-' }}</div>
          <div class="metric-label">健康度</div>
        </div>
        <div class="metric-card">
          <div class="metric-value">{{ coreMetrics.trafficPool || '-' }}</div>
          <div class="metric-label">流量池</div>
        </div>
      </div>

      <!-- AI 账号权重分析入口 -->
      <div v-if="weightResult" class="ai-analyze-section">
        <!-- AI 分析按钮：全宽突出显示，带火焰图标 -->
        <Button
          type="warning"
          size="large"
          long
          :loading="aiWeightAnalyzing"
          :disabled="aiWeightAnalyzing"
          @click="triggerAiWeightAnalysis"
          class="ai-analyze-btn"
        >
          <!-- 按钮图标 -->
          <Icon type="md-flame" />
          <!-- 根据状态显示不同文案 -->
          <span v-if="aiWeightAnalyzing" class="ai-btn-loading">AI 分析中... {{ aiWeightProgress }}%</span>
          <span v-else-if="aiWeightAnalyzed">已生成 AI 权重分析</span>
          <span v-else>🔥 AI 账号权重分析</span>
        </Button>
        <!-- 进度条：分析中显示 -->
        <Progress
          v-if="aiWeightAnalyzing"
          :percent="aiWeightProgress"
          :stroke-width="10"
          status="active"
          class="ai-analyze-progress"
        />
        <!-- 加载状态文字：分析中时显示，颜色加深的醒目文案 -->
        <p v-if="aiWeightAnalyzing" class="ai-analyze-loading-text">
          <Icon type="ios-flame" />
          <span>AI 正在深度分析账号权重数据，请耐心等待（{{ aiWeightProgress }}%）</span>
        </p>
        <!-- 提示文字：未分析时显示 -->
        <p v-if="!aiWeightAnalyzing && !aiWeightAnalyzed" class="ai-analyze-tip">
          点击按钮，由 AI 深度分析账号权重、给出涨粉策略与优化计划（约 30-90 秒）
        </p>
      </div>

      <!-- 详细数据表格 -->
      <div class="data-table-section">
        <div class="table-header">
          <h3>详细数据</h3>
          <div class="pagination">
            <Button size="small" :disabled="tableCurrentPage <= 1" @click="prevPage">
              上一页
            </Button>
            <span class="page-info">第 {{ tableCurrentPage }} / {{ totalPages }} 页</span>
            <Button size="small" :disabled="tableCurrentPage >= totalPages" @click="nextPage">
              下一页
            </Button>
          </div>
        </div>

        <Table
          :columns="tableColumns"
          :data="paginatedData"
          border
          size="small"
          class="custom-stripe-table"
        >
          <!-- 动态渲染所有数据 -->
          <template #rowValue="{ row }">
            <!-- 图片类型：封面图/视频数据分析 -->
            <div v-if="row.rowValueType === 1 && row.rowValueDetail && row.rowValueDetail.length > 0" class="image-grid">
              <div v-for="(item, index) in row.rowValueDetail" :key="index" class="image-item">
                <img :src="item.src || item.rowValue" :alt="item.name || row.rowName" class="grid-image" @click="showVideoAnalysis(item)" style="cursor: pointer;" />
                <p v-if="item.name" class="image-name">{{ item.name }}</p>
                <!-- 显示点赞、评论、分享数量 -->
                <div v-if="item.tips" class="image-stats">
                  <span class="stat-item" title="点赞">
                    <Icon type="md-heart" size="14" />
                    {{ getStatValue(item.tips, 'digg_count') }}
                  </span>
                  <span class="stat-item" title="评论">
                    <Icon type="md-chatbubbles" size="14" />
                    {{ getStatValue(item.tips, 'comment_count') }}
                  </span>
                  <span class="stat-item" title="分享">
                    <Icon type="md-share" size="14" />
                    {{ getStatValue(item.tips, 'share_count') }}
                  </span>
                </div>
              </div>
            </div>
            <!-- 单张图片 -->
            <div v-else-if="row.rowValue && String(row.rowValue).includes('/img/')" class="image-cell">
              <img :src="row.rowValue" :alt="row.rowName" class="level-image" />
            </div>
            <!-- 文本 -->
            <span v-else class="text-value">{{ row.rowValue !== null && row.rowValue !== undefined ? row.rowValue : '-' }}</span>
          </template>
        </Table>
      </div>

      <!-- ==================== AI 账号权重分析结果展示 ==================== -->
      <!-- 当 weightResult 中存在 AI 分析结果时展示，按 5 个区块渲染 -->
      <div v-if="hasAiWeightResult" class="ai-result-section">
        <!-- 区域标题 -->
        <div class="ai-result-header">
          <h3>🤖 AI 账号权重深度分析</h3>
          <Tag color="green">已完成</Tag>
        </div>

        <!-- 区块 1：权重综合诊断报告 -->
        <div v-if="weightResult.weight_analysis" class="ai-result-card">
          <h4 class="ai-result-title">📊 权重综合诊断</h4>
          <!-- 使用 CSS white-space: pre-wrap 保留 AI 返回文本中的换行格式 -->
          <div class="ai-result-content">{{ weightResult.weight_analysis }}</div>
        </div>

        <!-- 区块 2：涨粉与成长策略 -->
        <div v-if="weightResult.growth_suggestions" class="ai-result-card">
          <h4 class="ai-result-title">📈 涨粉与成长策略</h4>
          <div class="ai-result-content">{{ weightResult.growth_suggestions }}</div>
        </div>

        <!-- 区块 3：内容策略分析 -->
        <div v-if="weightResult.content_strategy" class="ai-result-card">
          <h4 class="ai-result-title">🎨 内容策略分析</h4>
          <div class="ai-result-content">{{ weightResult.content_strategy }}</div>
        </div>

        <!-- 区块 4：风险预警与合规建议 -->
        <div v-if="weightResult.risk_assessment" class="ai-result-card">
          <h4 class="ai-result-title">⚠️ 风险预警与合规建议</h4>
          <div class="ai-result-content">{{ weightResult.risk_assessment }}</div>
        </div>

        <!-- 区块 5：30天优化执行计划 -->
        <div v-if="weightResult.optimization_plan" class="ai-result-card">
          <h4 class="ai-result-title">🗓️ 30天优化执行计划</h4>
          <div class="ai-result-content">{{ weightResult.optimization_plan }}</div>
        </div>
      </div>
    </div>

    <!-- 查询历史表格 -->
    <div class="history-section" :class="{'mobile-history': isMobile}">
      <div class="history-header">
        <h3>查询历史</h3>
        <Button size="small" @click="loadHistory(1)" :loading="isLoadingHistory">
          <Icon type="ios-refresh" /> 刷新
        </Button>
      </div>

      <!-- 表格形式显示历史记录 -->
      <Table
        v-if="historyRecords.length > 0"
        :columns="mobileHistoryColumns"
        :data="historyRecords"
        :loading="isLoadingHistory"
        border
        size="small"
        class="history-stripe-table"
      >
        <template #avatar="{ row }">
          <Avatar :src="row.avatar_url || defaultAvatar" size="small" />
        </template>

        <template #nickname="{ row }">
          <span class="nickname-text">{{ row.nickname || '未知' }}</span>
        </template>

        <template #sec_uid="{ row }">
          <span class="sec-uid-text" :title="row.sec_uid">{{ row.sec_uid || '-' }}</span>
        </template>

        <template #weight_score="{ row }">
          <span class="score-text">{{ row.weight_score || 0 }}</span>
        </template>

        <template #weight_desc="{ row }">
          <Tag :color="getScoreTagColor(row.weight_score)">{{ row.weight_desc || '-' }}</Tag>
        </template>

        <template #health_score="{ row }">
          <span class="score-text">{{ row.health_score || 0 }}</span>
        </template>

        <template #health_desc="{ row }">
          <Tag :color="getScoreTagColor(row.health_score)">{{ row.health_desc || '-' }}</Tag>
        </template>

        <template #activity_score="{ row }">
          <span class="score-text">{{ row.activity_score || 0 }}</span>
        </template>

        <template #follower_count="{ row }">
          {{ formatNumber(row.follower_count) }}
        </template>

        <template #following_count="{ row }">
          {{ formatNumber(row.following_count) }}
        </template>

        <template #total_favorited="{ row }">
          {{ formatNumber(row.total_favorited) }}
        </template>

        <template #aweme_count="{ row }">
          {{ formatNumber(row.aweme_count) }}
        </template>

        <template #recent_7days_posts="{ row }">
          <Tag :color="row.recent_7days_posts > 0 ? 'green' : 'default'">{{ row.recent_7days_posts || 0 }}</Tag>
        </template>

        <template #engagement_rate="{ row }">
          <span class="score-text">{{ row.engagement_rate || '0.00' }}%</span>
        </template>

        <template #last_query_time="{ row }">
          <span class="time-text">{{ formatDate(row.last_query_time) }}</span>
        </template>

        <template #action="{ row }">
          <div class="action-btn-group">
            <Button size="small" type="info" @click="viewDetail(row)">
              查看详情
            </Button>
            <Button
              size="small"
              type="warning"
              @click="triggerHistoryAiWeightAnalysis(row)"
              :loading="aiWeightAnalyzing"
              :disabled="aiWeightAnalyzing"
            >
              AI分析
            </Button>
            <Button size="small" type="primary" @click="refreshRecord(row)" :loading="refreshingId === row.id">
              刷新
            </Button>
            <Button size="small" type="error" @click="deleteRecord(row.id)">
              删除
            </Button>
          </div>
        </template>
      </Table>

      <div v-else-if="!isLoadingHistory" class="empty-history">
        <Icon type="ios-folder-open" size="48" />
        <p>暂无查询记录</p>
      </div>

      <!-- 历史记录分页 -->
      <div v-if="totalRecords > pageSize" class="history-pagination">
        <div class="page-info">
          <span>共 {{ totalRecords }} 条记录</span>
        </div>
        <div class="page-buttons">
          <Button
            size="small"
            :disabled="historyCurrentPage <= 1"
            @click="loadHistory(historyCurrentPage - 1)"
          >
            上一页
          </Button>
          <Button
            size="small"
            :disabled="historyCurrentPage >= historyTotalPages"
            @click="loadHistory(historyCurrentPage + 1)"
          >
            下一页
          </Button>
        </div>
      </div>
    </div>

    <!-- Loading遮罩 -->
    <div v-if="isQuerying" class="loading-overlay">
      <div class="loading-spinner">
        <Icon type="ios-loading" size="32" class="spin-icon" />
        <p>正在查询账号权重...</p>
      </div>
    </div>

    <!-- 视频数据分析弹窗 -->
    <Modal
      v-model="videoAnalysisModal.visible"
      title="视频数据分析"
      width="1000"
      :footer-hide="true"
    >
      <div v-if="videoAnalysisModal.currentData" class="video-analysis-content">
        <!-- 封面图 -->
        <div class="analysis-section">
          <h4>📷 封面图</h4>
          <div class="cover-image-wrapper">
            <img :src="videoAnalysisModal.currentData.src" alt="封面图" class="cover-image" />
          </div>
        </div>

        <!-- 视频统计信息 -->
        <div class="analysis-section">
          <h4>📊 视频统计数据</h4>
          <div class="stats-grid">
            <div v-for="(stat, index) in parsedVideoStats" :key="index" class="stat-card">
              <div class="stat-label">{{ stat.label }}</div>
              <div class="stat-value">{{ stat.value }}</div>
            </div>
          </div>
        </div>

        <!-- 话题标签 -->
        <div class="analysis-section">
          <h4>🏷️ 话题标签</h4>
          <div class="topics-wrapper">
            <Tag v-for="(topic, index) in parsedTopics" :key="index" color="blue" style="margin: 5px;">{{ topic }}</Tag>
          </div>
        </div>

        <!-- 限制分析 -->
        <div class="analysis-section">
          <h4>⚠️ 限制分析</h4>
          <div v-if="parsedLimitAnalysis.length > 0" class="limit-info">
            <p><strong>限制原因：</strong></p>
            <ul>
              <li v-for="(limit, index) in parsedLimitAnalysis" :key="index">{{ limit }}</li>
            </ul>
          </div>
          <div v-else class="limit-info">
            <p>✅ 无明显限制</p>
          </div>
        </div>
      </div>
    </Modal>

    <!-- 详情弹窗 -->
    <Modal
      v-model="detailModal.visible"
      title="账号权重详情"
      width="900"
      :footer-hide="true"
    >
      <div v-if="detailModal.currentRecord" class="detail-content">
        <!-- 基本信息 -->
        <div class="detail-section">
          <h3 class="section-title">📋 基本信息</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">昵称：</span>
              <span class="info-value">{{ detailModal.currentRecord.nickname }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">抖音ID：</span>
              <span class="info-value">{{ detailModal.currentRecord.unique_id }}</span>
            </div>
            <div class="info-item full-width">
              <span class="info-label">SecUID：</span>
              <span class="info-value sec-uid">{{ detailModal.currentRecord.sec_uid }}</span>
            </div>
            <div class="info-item full-width">
              <span class="info-label">简介：</span>
              <span class="info-value">{{ detailModal.currentRecord.signature || '无' }}</span>
            </div>
          </div>
        </div>

        <!-- 核心指标 -->
        <div class="detail-section">
          <h3 class="section-title">📊 核心指标</h3>
          <div class="metrics-grid">
            <div class="metric-item">
              <div class="metric-value">{{ detailModal.currentRecord.weight_score || '-' }}</div>
              <div class="metric-label">权重分</div>
              <Tag :color="getScoreTagColor(detailModal.currentRecord.weight_score)" style="margin-top: 5px;">{{ detailModal.currentRecord.weight_desc }}</Tag>
            </div>
            <div class="metric-item">
              <div class="metric-value">{{ detailModal.currentRecord.health_score || '-' }}</div>
              <div class="metric-label">健康度</div>
              <Tag :color="getScoreTagColor(detailModal.currentRecord.health_score)" style="margin-top: 5px;">{{ detailModal.currentRecord.health_desc }}</Tag>
            </div>
            <div class="metric-item">
              <div class="metric-value">{{ detailModal.currentRecord.activity_score || '-' }}</div>
              <div class="metric-label">活跃度</div>
              <Tag :color="getScoreTagColor(detailModal.currentRecord.activity_score)" style="margin-top: 5px;">活跃</Tag>
            </div>
          </div>
        </div>

        <!-- 账号数据 -->
        <div class="detail-section">
          <h3 class="section-title">📈 账号数据</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">粉丝数：</span>
              <span class="info-value">{{ formatNumber(detailModal.currentRecord.follower_count) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">关注数：</span>
              <span class="info-value">{{ formatNumber(detailModal.currentRecord.following_count) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">总获赞：</span>
              <span class="info-value">{{ formatNumber(detailModal.currentRecord.total_favorited) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">作品数：</span>
              <span class="info-value">{{ formatNumber(detailModal.currentRecord.aweme_count) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">近7天投稿：</span>
              <span class="info-value">{{ detailModal.currentRecord.recent_7days_posts || 0 }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">互动率：</span>
              <span class="info-value">{{ detailModal.currentRecord.engagement_rate || '0.00' }}%</span>
            </div>
          </div>
        </div>

        <!-- 封面图和视频数据分析 -->
        <div class="detail-section" v-if="detailCoverImages.length > 0">
          <h3 class="section-title">📷 封面图和视频数据分析</h3>
          <div class="detail-image-grid">
            <div v-for="(item, index) in detailCoverImages" :key="index" class="detail-image-item">
              <img :src="item.src || item.rowValue" :alt="item.name" class="detail-grid-image" @click="showVideoAnalysis(item)" style="cursor: pointer;" />
              <p class="detail-image-name">{{ item.name }}</p>
              <!-- 显示点赞、评论、分享数量 -->
              <div v-if="item.tips" class="detail-image-stats">
                <span class="detail-stat-item" title="点赞">
                  <Icon type="md-heart" size="14" />
                  {{ getStatValue(item.tips, 'digg_count') }}
                </span>
                <span class="detail-stat-item" title="评论">
                  <Icon type="md-chatbubbles" size="14" />
                  {{ getStatValue(item.tips, 'comment_count') }}
                </span>
                <span class="detail-stat-item" title="分享">
                  <Icon type="md-share" size="14" />
                  {{ getStatValue(item.tips, 'share_count') }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- 详细数据表格 -->
        <div class="detail-section">
          <h3 class="section-title">📝 详细数据</h3>
          <div v-if="detailModal.currentRecord.weight_info" class="detail-table">
            <Table
              :columns="detailTableColumns"
              :data="parsedRowInfo"
              border
              stripe
              size="small"
            >
              <template #rowValue="{ row }">
                <div v-if="row.rowValue && String(row.rowValue).includes('/img/')" class="image-cell">
                  <img :src="row.rowValue" :alt="row.rowName" class="level-image" />
                </div>
                <span v-else>{{ row.rowValue || '-' }}</span>
              </template>
              <template #rowDesc="{ row }">
                <span :style="{ color: row.rowDescColorCode || '#999' }">{{ row.rowDesc || '-' }}</span>
              </template>
            </Table>
          </div>
        </div>

        <!-- ==================== AI 账号权重分析结果（详情弹窗） ==================== -->
        <!-- 当历史记录中存在 AI 分析字段时，在详情弹窗底部展示 -->
        <div
          v-if="detailModal.currentRecord.weight_analysis || detailModal.currentRecord.growth_suggestions || detailModal.currentRecord.content_strategy || detailModal.currentRecord.risk_assessment || detailModal.currentRecord.optimization_plan"
          class="detail-section"
        >
          <h3 class="section-title">🤖 AI 账号权重深度分析</h3>

          <!-- 权重综合诊断 -->
          <div v-if="detailModal.currentRecord.weight_analysis" class="ai-result-card">
            <h4 class="ai-result-title">📊 权重综合诊断</h4>
            <div class="ai-result-content">{{ detailModal.currentRecord.weight_analysis }}</div>
          </div>

          <!-- 涨粉与成长策略 -->
          <div v-if="detailModal.currentRecord.growth_suggestions" class="ai-result-card">
            <h4 class="ai-result-title">📈 涨粉与成长策略</h4>
            <div class="ai-result-content">{{ detailModal.currentRecord.growth_suggestions }}</div>
          </div>

          <!-- 内容策略分析 -->
          <div v-if="detailModal.currentRecord.content_strategy" class="ai-result-card">
            <h4 class="ai-result-title">🎨 内容策略分析</h4>
            <div class="ai-result-content">{{ detailModal.currentRecord.content_strategy }}</div>
          </div>

          <!-- 风险预警 -->
          <div v-if="detailModal.currentRecord.risk_assessment" class="ai-result-card">
            <h4 class="ai-result-title">⚠️ 风险预警与合规建议</h4>
            <div class="ai-result-content">{{ detailModal.currentRecord.risk_assessment }}</div>
          </div>

          <!-- 30天优化计划 -->
          <div v-if="detailModal.currentRecord.optimization_plan" class="ai-result-card">
            <h4 class="ai-result-title">🗓️ 30天优化执行计划</h4>
            <div class="ai-result-content">{{ detailModal.currentRecord.optimization_plan }}</div>
          </div>
        </div>

        <!-- 查询信息 -->
        <div class="detail-section">
          <h3 class="section-title">🕒 查询信息</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">最后查询：</span>
              <span class="info-value">{{ formatDate(detailModal.currentRecord.last_query_time) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">创建时间：</span>
              <span class="info-value">{{ formatDate(detailModal.currentRecord.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>
    </Modal>

    <!-- ==================== AI 分析错误弹窗 ==================== -->
    <!-- 当 AI 分析失败、超时或任务过期时弹出，提示用户原因 -->
    <Modal
      v-model="aiWeightErrorDialogVisible"
      :title="aiWeightErrorDialogTitle"
      width="400"
      @on-ok="closeAiWeightErrorDialog"
    >
      <!-- 弹窗内容 -->
      <p>{{ aiWeightErrorDialogContent }}</p>
      <!-- 自定义底部按钮：仅显示确定 -->
      <template #footer>
        <Button type="primary" @click="closeAiWeightErrorDialog">确定</Button>
      </template>
    </Modal>
  </div>
</template>

<script>
import apiClient from '@/api/index';
import { Signature } from '@/utils/signature';

export default {
  name: 'WeightQuery',
  data() {
    return {
      // 移动端相关
      isMobile: false,          // 是否为移动设备
      mobileWidth: 768,         // 移动端断点
      
      // 输入值
      inputValue: '',

      // 查询状态
      isQuerying: false,
      isLoadingHistory: false,
      refreshingId: null,

      // 查询结果
      weightResult: null,
      detailModal: {
        visible: false,
        currentRecord: null
      },
      
      // 视频数据分析弹窗
      videoAnalysisModal: {
        visible: false,
        currentData: null
      },

      // ==================== AI 账号权重分析相关状态 ====================
      // AI 分析是否正在进行中（控制按钮 loading 和禁用状态）
      aiWeightAnalyzing: false,
      // AI 分析进度条百分比（0-100），用于给用户视觉反馈
      aiWeightProgress: 0,
      // 当前账号是否已完成 AI 权重分析（用于控制结果区域显示）
      aiWeightAnalyzed: false,
      // AI 分析错误弹窗是否显示
      aiWeightErrorDialogVisible: false,
      // AI 分析错误弹窗标题
      aiWeightErrorDialogTitle: 'AI分析提示',
      // AI 分析错误弹窗内容
      aiWeightErrorDialogContent: '',

      // 详情弹窗表格列
      detailTableColumns: [
        {
          title: '项目名称',
          key: 'rowName',
          width: 150,
          align: 'center'
        },
        {
          title: '数据值',
          key: 'rowValue',
          minWidth: 200,
          slot: 'rowValue'
        },
        {
          title: '优化建议',
          key: 'rowDesc',
          minWidth: 200,
          slot: 'rowDesc'
        }
      ],

      // 详细数据分页
      tableCurrentPage: 1,
      tablePageSize: 10,

      // 历史记录表格列定义
      historyColumns: [
        {
          title: '头像',
          key: 'avatar',
          width: 70,
          align: 'center',
          slot: 'avatar'
        },
        {
          title: '昵称',
          key: 'nickname',
          width: 120,
          slot: 'nickname'
        },
        {
          title: '抖音ID',
          key: 'unique_id',
          width: 110
        },
        {
          title: 'SecUID',
          key: 'sec_uid',
          width: 120,
          ellipsis: true,
          tooltip: true
        },
        {
          title: '简介',
          key: 'signature',
          width: 150,
          ellipsis: true,
          tooltip: true
        },
        {
          title: '权重分',
          key: 'weight_score',
          width: 80,
          align: 'center',
          slot: 'weight_score'
        },
        {
          title: '权重描述',
          key: 'weight_desc',
          width: 180,
          align: 'center',
          slot: 'weight_desc',
          ellipsis: true,
          tooltip: true
        },
        {
          title: '健康度',
          key: 'health_score',
          width: 100,
          align: 'center',
          slot: 'health_score'
        },
        {
          title: '健康描述',
          key: 'health_desc',
          width: 150,
          align: 'center',
          slot: 'health_desc',
          ellipsis: true,
          tooltip: true
        },
        {
          title: '活跃度',
          key: 'activity_score',
          width: 80,
          align: 'center',
          slot: 'activity_score'
        },
        {
          title: '粉丝数',
          key: 'follower_count',
          width: 80,
          align: 'center',
          slot: 'follower_count'
        },
        {
          title: '关注数',
          key: 'following_count',
          width: 80,
          align: 'center',
          slot: 'following_count'
        },
        {
          title: '总获赞',
          key: 'total_favorited',
          width: 80,
          align: 'center',
          slot: 'total_favorited'
        },
        {
          title: '作品数',
          key: 'aweme_count',
          width: 80,
          align: 'center',
          slot: 'aweme_count'
        },
        {
          title: '近7天投稿',
          key: 'recent_7days_posts',
          width: 90,
          align: 'center',
          slot: 'recent_7days_posts'
        },
        {
          title: '互动率',
          key: 'engagement_rate',
          width: 80,
          align: 'center',
          slot: 'engagement_rate'
        },
        {
          title: '最后查询',
          key: 'last_query_time',
          width: 160,
          slot: 'last_query_time'
        },
        {
          title: '操作',
          key: 'action',
          width: 260,
          align: 'center',
          fixed: 'right',
          slot: 'action'
        }
      ],

      // 详细数据表格列
      tableColumns: [
        {
          title: '项目名称',
          key: 'rowName',
          width: 150,
          align: 'center'
        },
        {
          title: '数据值',
          key: 'rowValue',
          minWidth: 200,
          slot: 'rowValue'
        },
        {
          title: '优化建议',
          key: 'rowDesc',
          minWidth: 200,
          render: (h, params) => {
            const desc = params.row.rowDesc;
            const colorCode = params.row.rowDescColorCode || '#999';
            return h('span', {
              style: { color: desc ? colorCode : '#999' }
            }, desc || '-');
          }
        }
      ],

      // 历史记录
      historyRecords: [],
      historyCurrentPage: 1,
      pageSize: 10,
      totalRecords: 0,

      // VIP状态和余额
      vipStatus: {
        is_vip: false,
        is_super_vip: false,
        package_name: '',
        end_date: '',
        expire_time: '',
        start_date: '',
        days_left: 0,
        daily_limit: 0,
        used_today: 0,
        remaining_today: 0
      },
      queryBalance: 0,
      remainingQueries: 0,
      queryCost: 0,

      // 站点信息
      siteInfo: {
        allow_point_recharge: true
      },
      isSiteInfoLoaded: false,

      // 默认头像
      defaultAvatar: require('@/assets/images/default-avatar.png'),

      // 主题相关
      currentTheme: 'starry',
      themeOptions: [
        { name: 'default', label: '经典' },
        { name: 'starry', label: '星空' },
        { name: 'blue', label: '蓝色' },
        { name: 'purple', label: '紫色' },
        { name: 'green', label: '绿色' },
        { name: 'red', label: '红色' },
        { name: 'orange', label: '橙色' }
      ],

      // API基础URL
      apiBaseUrl: '',

      // 防抖定时器
      resizeTimer: null
    };
  },

  computed: {
    // 核心指标（从 rowInfo 中提取）
    coreMetrics() {
      if (!this.weightResult || !this.weightResult.rowInfo) {
        return { weightScore: '-', activity: '-', health: '-', trafficPool: '-' };
      }

      const metrics = { weightScore: '-', activity: '-', health: '-', trafficPool: '-' };

      this.weightResult.rowInfo.forEach(item => {
        switch (item.rowName) {
          case '权重分':
            metrics.weightScore = item.rowValue;
            break;
          case '活跃度':
            metrics.activity = item.rowValue;
            break;
          case '健康度':
            metrics.health = item.rowValue;
            break;
          case '流量池':
            metrics.trafficPool = item.rowValue;
            break;
        }
      });

      return metrics;
    },

    // 历史记录表格列（根据isMobile动态调整）
    mobileHistoryColumns() {
      if (this.isMobile) {
        // 移动端：只显示关键列
        return [
          {
            title: '头像',
            key: 'avatar',
            width: 60,
            align: 'center',
            slot: 'avatar'
          },
          {
            title: '昵称',
            key: 'nickname',
            minWidth: 80,
            slot: 'nickname'
          },
          {
            title: '权重',
            key: 'weight_score',
            width: 60,
            align: 'center',
            slot: 'weight_score'
          },
          {
            title: '操作',
            key: 'action',
            width: 200,
            align: 'center',
            fixed: 'right',
            slot: 'action'
          }
        ];
      } else {
        // PC端：显示所有列
        return this.historyColumns;
      }
    },

    // 详细数据分页
    paginatedData() {
      if (!this.weightResult || !this.weightResult.rowInfo) return [];
      const start = (this.tableCurrentPage - 1) * this.tablePageSize;
      const end = start + this.tablePageSize;
      return this.weightResult.rowInfo.slice(start, end);
    },
    
    totalPages() {
      if (!this.weightResult || !this.weightResult.rowInfo) return 1;
      return Math.ceil(this.weightResult.rowInfo.length / this.tablePageSize) || 1;
    },

    // 主题名称
    currentThemeName() {
      const theme = this.themeOptions.find(t => t.name === this.currentTheme);
      return theme ? theme.label : '经典';
    },

    // 解析 row_info JSON 字符串
    parsedRowInfo() {
      if (!this.detailModal.currentRecord || !this.detailModal.currentRecord.row_info) {
        return [];
      }
      try {
        return JSON.parse(this.detailModal.currentRecord.row_info);
      } catch (e) {
        console.error('解析 row_info 失败:', e);
        return [];
      }
    },

    // 从 row_info 中提取封面图和视频数据分析
    detailCoverImages() {
      const rowInfo = this.parsedRowInfo;
      if (!rowInfo || rowInfo.length === 0) return [];
      
      // 找到 rowName 为 "封面图和视频数据分析" 的项
      const coverImageRow = rowInfo.find(row => row.rowName === '封面图和视频数据分析');
      if (!coverImageRow || !coverImageRow.rowValueDetail) return [];
      
      return coverImageRow.rowValueDetail;
    },

    // 历史记录分页
    historyTotalPages() {
      return Math.ceil(this.totalRecords / this.pageSize) || 1;
    },

    // ==================== AI 账号权重分析相关计算属性 ====================

    /**
     * 判断当前查询结果中是否存在 AI 权重分析结果
     * 只要 5 个字段中任意一个非空，即认为存在 AI 结果
     */
    hasAiWeightResult() {
      // 没有查询结果时直接返回 false
      if (!this.weightResult) return false;
      // 检查 5 个 AI 字段是否有内容
      return !!(
        this.weightResult.weight_analysis ||
        this.weightResult.growth_suggestions ||
        this.weightResult.content_strategy ||
        this.weightResult.risk_assessment ||
        this.weightResult.optimization_plan
      );
    }
  },

  mounted() {
    this.initTheme();
    this.checkLoginAndLoadData();
    this.loadHistory(1);
    this.fetchSiteInfo();
    this.checkMobile();
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    // 清理 body 的主题类名
    document.body.className = '';
    window.removeEventListener('resize', this.handleResize);
  },

  watch: {
    currentTheme(newVal) {
      document.body.className = `theme-${newVal}`;
    }
  },

  methods: {
    /**
     * 检测是否为移动端
     */
    checkMobile() {
      this.isMobile = window.innerWidth <= this.mobileWidth;
    },

    /**
     * 处理窗口大小变化
     */
    handleResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.checkMobile();
      }, 200);
    },

    /**
     * 初始化主题
     */
    initTheme() {
      const savedTheme = localStorage.getItem('currentTheme');
      if (savedTheme) {
        this.currentTheme = savedTheme;
        console.log('✅ 加载保存的主题:', savedTheme);
      } else {
        // 默认使用星空模式
        this.currentTheme = 'starry';
        localStorage.setItem('currentTheme', 'starry');
        console.log('✅ 设置默认主题为: starry');
      }
      // 将主题类名应用到 body 元素，确保全局样式生效
      document.body.className = `theme-${this.currentTheme}`;
    },

    /**
     * 切换主题
     */
    handleThemeChange(themeName) {
      this.currentTheme = themeName;
      localStorage.setItem('currentTheme', themeName);
      // 同时将主题类名添加到 body 元素
      document.body.className = `theme-${themeName}`;
      // 触发自定义事件通知父组件
      window.dispatchEvent(new CustomEvent('theme-changed', { detail: { theme: themeName } }));
      this.$Message.success(`已切换到${this.currentThemeName}主题`);
    },

    /**
     * 检查登录状态并加载数据
     */
    async checkLoginAndLoadData() {
      const isUserLoggedIn = localStorage.getItem('userLoggedIn') === 'true';
      const userEmail = localStorage.getItem('userEmail') || '';
      
      console.log('用户登录状态:', isUserLoggedIn);
      console.log('userEmail:', userEmail);
      
      if (isUserLoggedIn && userEmail) {
        await this.checkVipStatus(userEmail);
        await this.fetchUserBalance(userEmail);
      } else {
        console.log('用户未登录或没有邮箱信息');
      }
    },

    /**
     * 获取用户余额
     */
    async fetchUserBalance(email) {
      try {
        const requestData = { user_email: email };
        const timestamp = Math.floor(Date.now() / 1000);
        const signature = Signature.generateSignature(requestData, timestamp);
        
        const response = await apiClient.post('user/balance', requestData, {
          headers: {
            'X-Signature': signature,
            'X-Timestamp': timestamp
          }
        });
        if (response.data.code === 200) {
          this.queryBalance = response.data.data.balance || 0;
        }
      } catch (error) {
        console.error('获取余额失败:', error);
      }
    },

    /**
     * 检查VIP状态
     */
    async checkVipStatus(email) {
      try {
        const requestData = { user_email: email };
        const timestamp = Math.floor(Date.now() / 1000);
        const signature = Signature.generateSignature(requestData, timestamp);
        
        const response = await apiClient.get('vip/status', {
          params: requestData,
          headers: {
            'X-Signature': signature,
            'X-Timestamp': timestamp
          }
        });
        console.log('VIP状态API返回:', response.data);
        if (response.data.code === 200) {
          this.vipStatus = {
            ...this.vipStatus,
            ...response.data.data
          };
          console.log('更新后的VIP状态:', this.vipStatus);
          this.$forceUpdate();
        }
      } catch (error) {
        console.error('检查VIP状态失败:', error);
      }
    },

    /**
     * 获取站点信息
     */
    async fetchSiteInfo() {
      try {
        const response = await apiClient.get('admin/getsite');
        if (response.data.code === 200) {
          this.apiBaseUrl = response.data.data.api_domain || '';
          this.siteInfo = response.data.data;
          this.isSiteInfoLoaded = true;
        }
      } catch (error) {
        console.error('获取站点信息失败:', error);
      }
    },

    /**
     * 查询权重
     */
    async queryWeight() {
      if (!this.inputValue.trim()) {
        this.$Message.warning('请输入抖音链接、分享链接或sec_uid');
        return;
      }

      const userEmail = localStorage.getItem('userEmail') || '';
      const isUserLoggedIn = localStorage.getItem('userLoggedIn') === 'true';
      
      if (!isUserLoggedIn || !userEmail) {
        this.$Message.error('请先登录');
        return;
      }

      this.isQuerying = true;

      try {
        const requestData = {
          user_email: userEmail,
          input: this.inputValue.trim()
        };
        const timestamp = Math.floor(Date.now() / 1000);
        const signature = Signature.generateSignature(requestData, timestamp);
        
        const response = await apiClient.post('v2/query/weight', requestData, {
          headers: {
            'X-Signature': signature,
            'X-Timestamp': timestamp
          }
        });

        if (response.data.code === 200) {
          // 解析嵌套的返回数据结构 - 新结构：data.weight_info.data
          const responseData = response.data.data;
          
          // 处理新的嵌套结构
          let weightInfoData;
          if (responseData.weight_info && responseData.weight_info.code === 200) {
            // 新结构：weight_info 内部还有 code 和 data
            weightInfoData = responseData.weight_info.data;
          } else {
            // 旧结构：直接使用 weight_info
            weightInfoData = responseData.weight_info?.data || responseData.weight_info;
          }
          
          this.weightResult = weightInfoData;

          // 如果后端返回的历史记录中已包含 AI 分析结果，同步设置分析完成状态
          // 这样用户查询后无需重新点击 AI 分析按钮即可看到历史分析内容
          if (this.weightResult && (this.weightResult.isAiWeightAnalyzed || this.hasAiWeightResult)) {
            this.aiWeightAnalyzed = true;
          } else {
            this.aiWeightAnalyzed = false;
          }
          
          // 更新余额和查询次数
          this.queryBalance = responseData.new_balance || 0;
          this.remainingQueries = responseData.remaining_queries || 0;
          this.queryCost = responseData.cost || 0;
          
          // 更新VIP状态
          if (responseData.is_vip !== undefined) {
            this.vipStatus.is_vip = responseData.is_vip;
          }
          
          console.log('查询结果:', this.weightResult);
          console.log('核心指标:', this.coreMetrics);
          console.log('剩余查询次数:', this.remainingQueries);
          console.log('本次消耗:', this.queryCost);
          this.$Message.success(`查询成功${this.queryCost > 0 ? '，消耗' + this.queryCost + '点数' : ''}`);
          this.loadHistory(1);
        } else {
          this.$Message.error(response.data.msg || '查询失败');
        }
      } catch (error) {
        console.error('查询权重失败:', error);
        this.$Message.error('查询失败，请稍后重试');
      } finally {
        this.isQuerying = false;
      }
    },

    /**
     * 加载历史记录
     */
    async loadHistory(page = 1) {
      this.isLoadingHistory = true;
      this.historyCurrentPage = page;

      const userEmail = localStorage.getItem('userEmail') || '';

      try {
        const requestData = {
          user_email: userEmail,
          page: page,
          page_size: this.pageSize
        };
        const timestamp = Math.floor(Date.now() / 1000);
        const signature = Signature.generateSignature(requestData, timestamp);
        
        // 改为 POST 请求
        const response = await apiClient.post('v2/query/weight/history', requestData, {
          headers: {
            'X-Signature': signature,
            'X-Timestamp': timestamp
          }
        });

        if (response.data.code === 200) {
          // 修改：使用 list 而不是 records
          this.historyRecords = response.data.data.list || [];
          this.totalRecords = response.data.data.total || 0;
          console.log('历史记录:', this.historyRecords);
        }
      } catch (error) {
        console.error('加载历史记录失败:', error);
      } finally {
        this.isLoadingHistory = false;
      }
    },

    /**
     * 上一页
     */
    prevPage() {
      if (this.tableCurrentPage > 1) {
        this.tableCurrentPage--;
      }
    },

    /**
     * 下一页
     */
    nextPage() {
      if (this.tableCurrentPage < this.totalPages) {
        this.tableCurrentPage++;
      }
    },

    /**
     * 刷新记录
     */
    async refreshRecord(row) {
      this.refreshingId = row.id;
      const userEmail = localStorage.getItem('userEmail') || '';

      try {
        // 从 row 对象中获取 sec_uid
        const secUid = row.sec_uid;
        
        if (!secUid) {
          this.$Message.error('缺少 sec_uid 参数');
          return;
        }

        const requestData = {
          user_email: userEmail,
          sec_uids: [secUid]  // 后端需要 sec_uids 数组
        };
        
        const timestamp = Math.floor(Date.now() / 1000);
        const signature = Signature.generateSignature(requestData, timestamp);
        
        // 调用刷新接口
        const response = await apiClient.post('v2/query/weight/refresh', requestData, {
          headers: {
            'X-Signature': signature,
            'X-Timestamp': timestamp
          }
        });

        if (response.data.code === 200) {
          const result = response.data.data;
          
          if (result.success_count > 0) {
            this.$Message.success('刷新成功');
            // 更新余额
            this.queryBalance = result.new_balance;
            // 刷新历史记录列表
            this.loadHistory(this.historyCurrentPage);
            
            // 如果有成功的数据，更新查询结果
            if (this.weightResult && this.weightResult.sec_uid === secUid) {
              // 重新查询当前显示的数据
              await this.queryWeightBySecUid(secUid);
            }
          } else {
            this.$Message.error('刷新失败：' + (result.results[0]?.message || '未知错误'));
          }
        } else {
          this.$Message.error(response.data.msg || '刷新失败');
        }
      } catch (error) {
        console.error('刷新记录失败:', error);
        this.$Message.error('刷新失败，请稍后重试');
      } finally {
        this.refreshingId = null;
      }
    },

    /**
     * 根据 sec_uid 查询权重
     */
    async queryWeightBySecUid(secUid) {
      try {
        const userEmail = localStorage.getItem('userEmail') || '';
        const requestData = {
          user_email: userEmail,
          input: secUid
        };
        const timestamp = Math.floor(Date.now() / 1000);
        const signature = Signature.generateSignature(requestData, timestamp);
        
        const response = await apiClient.post('v2/query/weight', requestData, {
          headers: {
            'X-Signature': signature,
            'X-Timestamp': timestamp
          }
        });

        if (response.data.code === 200) {
          const responseData = response.data.data;
          let weightInfoData;
          if (responseData.weight_info && responseData.weight_info.code === 200) {
            weightInfoData = responseData.weight_info.data || responseData.weight_info;
          } else {
            weightInfoData = responseData;
          }
          this.weightResult = weightInfoData;
        }
      } catch (error) {
        console.error('查询权重失败:', error);
      }
    },

    /**
     * 删除记录
     */
    deleteRecord(recordId) {
      const userEmail = localStorage.getItem('userEmail') || '';
      
      this.$Modal.confirm({
        title: '确认删除',
        content: '确定要删除这条查询记录吗？',
        onOk: async () => {
          try {
            const requestData = {
              user_email: userEmail,
              record_id: recordId
            };
            const timestamp = Math.floor(Date.now() / 1000);
            const signature = Signature.generateSignature(requestData, timestamp);
            
            const response = await apiClient.post('v2/query/weight/delete', requestData, {
              headers: {
                'X-Signature': signature,
                'X-Timestamp': timestamp
              }
            });

            if (response.data.code === 200) {
              this.$Message.success('删除成功');
              this.loadHistory(this.historyCurrentPage);
            } else {
              this.$Message.error(response.data.msg || '删除失败');
            }
          } catch (error) {
            console.error('删除记录失败:', error);
            this.$Message.error('删除失败，请稍后重试');
          }
        }
      });
    },

    /**
     * 查看详情
     */
    viewDetail(record) {
      this.detailModal.currentRecord = record;
      this.detailModal.visible = true;
    },

    /**
     * 跳转充值页面
     */
    recharge() {
      this.$router.push({ name: 'Recharge' });
    },

    /**
     * 格式化数字
     */
    formatNumber(num) {
      if (!num) return '0';
      if (num >= 100000000) {
        return (num / 100000000).toFixed(1) + '亿';
      } else if (num >= 10000) {
        return (num / 10000).toFixed(1) + '万';
      }
      return num.toString();
    },

    /**
     * 格式化日期
     */
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleString('zh-CN');
    },

    /**
     * 从 tips JSON 中获取统计值
     */
    getStatValue(tips, statKey) {
      if (!tips) return 0;
      try {
        const data = JSON.parse(tips);
        const stats = data.video_stats?.[0]?.statistics;
        if (!stats) return 0;
        return stats[statKey] || 0;
      } catch (e) {
        return 0;
      }
    },

    /**
     * 格式化粉丝数据
     */
    formatFansData(value) {
      if (!value) return '-';
      try {
        const data = JSON.parse(value);
        return data.map(item => `${item.name}: ${(item.value * 100).toFixed(1)}%`).join(', ');
      } catch (e) {
        return value;
      }
    },

    /**
     * 显示视频数据分析
     */
    showVideoAnalysis(item) {
      if (!item.tips) {
        this.$Message.warning('暂无数据分析');
        return;
      }
      
      try {
        const analysisData = JSON.parse(item.tips);
        this.videoAnalysisModal.currentData = {
          src: item.src,
          name: item.name,
          ...analysisData
        };
        this.videoAnalysisModal.visible = true;
      } catch (e) {
        console.error('解析视频数据失败:', e);
        this.$Message.error('数据解析失败');
      }
    },

    /**
     * 解析视频统计数据
     */
    parsedVideoStats() {
      if (!this.videoAnalysisModal.currentData || !this.videoAnalysisModal.currentData.video_stats) {
        return [];
      }
      
      try {
        const stats = this.videoAnalysisModal.currentData.video_stats[0]?.statistics;
        if (!stats) return [];
        
        return [
          { label: '推荐数', value: stats.recommend_count || 0 },
          { label: '评论数', value: stats.comment_count || 0 },
          { label: '点赞数', value: stats.digg_count || 0 },
          { label: '分享数', value: stats.share_count || 0 },
          { label: '收藏数', value: stats.collect_count || 0 },
          { label: '赞赏数', value: stats.admire_count || 0 }
        ];
      } catch (e) {
        return [];
      }
    },

    /**
     * 解析话题标签
     */
    parsedTopics() {
      if (!this.videoAnalysisModal.currentData || !this.videoAnalysisModal.currentData.topics) {
        return [];
      }
      return this.videoAnalysisModal.currentData.topics;
    },

    /**
     * 解析限制分析
     */
    parsedLimitAnalysis() {
      if (!this.videoAnalysisModal.currentData || !this.videoAnalysisModal.currentData.limit_analysis) {
        return [];
      }
      
      try {
        const limits = this.videoAnalysisModal.currentData.limit_analysis[0]?.limit_reasons;
        return limits || [];
      } catch (e) {
        return [];
      }
    },

    /**
     * 获取权重等级颜色
     */
    getWeightLevelColor(level) {
      const colorMap = {
        '高权重': 'red',
        '中高权重': 'orange',
        '普通权重': 'default',
        '低权重': 'gray'
      };
      return colorMap[level] || 'default';
    },

    /**
     * 获取分数对应的标签颜色
     * @param {number|string} score - 分数值
     */
    getScoreTagColor(score) {
      const numScore = Number(score);
      if (numScore >= 80) return 'green';
      if (numScore >= 60) return 'blue';
      if (numScore >= 40) return 'orange';
      return 'red';
    },

    // ==================== AI 账号权重分析方法 ====================

    /**
     * 从 rowInfo 中提取指定名称的字段值
     * 用于构造 weight_data 时提取「权重分」「活跃度」「健康度」等核心指标
     *
     * @param {string} name - 字段名称（如 '权重分'）
     * @returns {string} 字段值，找不到时返回 '未知'
     */
    getRowItemValue(name) {
      // 没有查询结果或没有 rowInfo 数组时返回默认值
      if (!this.weightResult || !this.weightResult.rowInfo) return '未知';
      // 在 rowInfo 数组中查找 rowName 匹配的项
      const item = this.weightResult.rowInfo.find(r => r.rowName === name);
      // 找到则返回 rowValue，否则返回 '未知'
      return item ? item.rowValue : '未知';
    },

    /**
     * 从查询历史记录行触发 AI 账号权重分析
     *
     * 与当前查询结果的分析共用同一套异步轮询逻辑。
     * 先把历史行数据映射为 weightResult 格式，再调用 triggerAiWeightAnalysis()。
     *
     * @param {Object} row - 查询历史表格的单行数据，包含 sec_uid、nickname、
     *   unique_id、signature、avatar_url、row_info（JSON 字符串）等字段
     */
    triggerHistoryAiWeightAnalysis(row) {
      // 校验 1：必须有行数据
      if (!row) {
        this.$Message.warning('历史记录数据无效');
        return;
      }
      // 校验 2：防止重复点击（AI 分析进行中时禁止再次触发）
      if (this.aiWeightAnalyzing) return;

      // ========== 解析 row_info 获取 rowInfo 数组 ==========
      // rowInfo 包含「权重分」「活跃度」「资料完整度」「健康度」等详细指标
      let rowInfo = [];
      if (row.row_info) {
        try {
          // row_info 可能是 JSON 字符串或已解析的对象
          rowInfo = typeof row.row_info === 'string' ? JSON.parse(row.row_info) : row.row_info;
        } catch (e) {
          console.error('解析历史记录 row_info 失败:', e);
          rowInfo = [];
        }
      }

      // 如果没有 rowInfo，尝试从 weight_info 解析
      if (rowInfo.length === 0 && row.weight_info) {
        try {
          const wi = typeof row.weight_info === 'string' ? JSON.parse(row.weight_info) : row.weight_info;
          if (wi && wi.rowInfo) {
            rowInfo = wi.rowInfo;
          }
        } catch (e) {
          // 忽略解析错误
        }
      }

      // ========== 构造 weightResult 兼容对象 ==========
      // 将历史记录的扁平字段映射为 weightResult 所需的嵌套结构
      this.weightResult = {
        secUserId: row.sec_uid || '',                 // 抖音账号唯一标识
        nickname: row.nickname || '',                  // 账号昵称
        account: row.unique_id || '',                  // 抖音号
        desc: row.signature || '',                     // 账号简介
        avatarUrl: row.avatar_url || '',               // 头像 URL
        rowInfo: rowInfo,                              // 详细数据数组
        // 已有的 AI 分析字段也带过来（历史记录中可能已有）
        weight_analysis: row.weight_analysis || '',
        growth_suggestions: row.growth_suggestions || '',
        content_strategy: row.content_strategy || '',
        risk_assessment: row.risk_assessment || '',
        optimization_plan: row.optimization_plan || '',
        isAiWeightAnalyzed: !!(                         // 标记是否已有 AI 分析结果
          row.weight_analysis || row.growth_suggestions ||
          row.content_strategy || row.risk_assessment || row.optimization_plan
        )
      };

      // 重置分析状态，准备开始新一轮分析
      this.aiWeightAnalyzed = false;

      // 滚动到页面顶部，让用户看到结果区域（含进度条）
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // 调用共享的 AI 分析逻辑
        this.triggerAiWeightAnalysis();
      });
    },

    /**
     * 触发 AI 账号权重深度分析（异步轮询版）
     *
     * 流程：
     *   1. 校验登录状态和权重数据
     *   2. 发起异步分析请求，获得 task_id（1 秒内返回，不会超时）
     *   3. 每 5 秒轮询一次结果，最多 60 次（5 分钟）
     *   4. 获取结果后更新 weightResult 并展示 5 个 AI 分析区块
     *
     * 此模式解决了 PHP 100 秒执行超时问题，参考 TiktokQuery.vue 的 AI 作品分析实现
     */
    async triggerAiWeightAnalysis() {
      // 校验 1：必须有查询结果才能分析
      if (!this.weightResult) {
        this.$Message.warning('请先查询账号权重数据');
        return;
      }
      // 校验 2：防止重复点击（仅分析中时不允许再次点击）
      if (this.aiWeightAnalyzing) return;

      // 获取当前登录用户邮箱，用于签名和参数
      const userEmail = localStorage.getItem('userEmail') || '';
      // 获取登录状态
      const isUserLoggedIn = localStorage.getItem('userLoggedIn') === 'true';
      // 未登录时提示并退出
      if (!isUserLoggedIn || !userEmail) {
        this.$Message.error('请先登录');
        return;
      }

      // 标记分析开始，重置进度条为 5%
      this.aiWeightAnalyzing = true;
      this.aiWeightProgress = 5;

      // 启动进度条动画：每 1.5 秒增加随机进度，给用户等待反馈
      // 进度上限为 93%，真实完成时再跳到 100%
      const progressTimer = setInterval(() => {
        if (this.aiWeightProgress < 93) {
          // 每次增加 2-6 的随机值
          this.aiWeightProgress += Math.floor(Math.random() * 5) + 2;
          // 防止超过 93%
          if (this.aiWeightProgress > 93) this.aiWeightProgress = 93;
        }
      }, 1500);

      try {
        // ========== 第一步：构造请求参数并计算签名 ==========
        // 构造 weight_data，字段名与小程序端保持一致，确保后端 AgnesAiService 能正确解析
        const weightData = {
          secUserId: this.weightResult.secUserId,                    // 抖音账号唯一标识
          nickname: this.weightResult.nickname || '',                // 账号昵称
          account: this.weightResult.account || '',                  // 抖音号
          desc: this.weightResult.desc || '',                        // 账号简介
          avatarUrl: this.weightResult.avatarUrl || '',              // 头像 URL
          weightScore: this.getRowItemValue('权重分'),               // 权重分（从 rowInfo 提取）
          activity: this.getRowItemValue('活跃度'),                  // 活跃度（从 rowInfo 提取）
          dataIntegrity: this.getRowItemValue('资料完整度'),         // 资料完整度（从 rowInfo 提取）
          health: this.getRowItemValue('健康度'),                    // 健康度（从 rowInfo 提取）
          rowInfo: this.weightResult.rowInfo || []                   // 完整 rowInfo 数组，供 AI prompt 使用
        };

        // 构造请求体，包含 weight_data 和 user_email
        const requestData = {
          weight_data: weightData,
          user_email: userEmail
        };
        // 生成当前时间戳（秒级）
        const timestamp = Math.floor(Date.now() / 1000);
        // 使用 Signature 工具生成签名
        const signature = Signature.generateSignature(requestData, timestamp);

        // ========== 第二步：发起异步 AI 权重分析请求 ==========
        // POST /api/v2/query/ai/analyze_weight
        const asyncResp = await apiClient.post('v2/query/ai/analyze_weight', requestData, {
          headers: {
            'X-Signature': signature,
            'X-Timestamp': timestamp
          }
        });

        // 解析响应（兼容 apiClient 不同返回格式）
        let asyncData = asyncResp;
        if (asyncResp && asyncResp.data) {
          asyncData = asyncResp.data;
        }
        console.log('[AI权重异步] 创建任务响应:', asyncData);

        // 异步任务创建失败：显示错误弹窗并退出
        if (!asyncData || asyncData.code !== 200 || !asyncData.data || !asyncData.data.task_id) {
          const msg = (asyncData && asyncData.msg) || 'AI权重分析任务创建失败';
          this.showAiWeightErrorDialog('AI分析提示', msg);
          // 停止进度条动画
          clearInterval(progressTimer);
          // 重置分析状态
          this.aiWeightAnalyzing = false;
          this.aiWeightProgress = 0;
          return;
        }

        // 拿到 task_id，进入轮询阶段
        const taskId = asyncData.data.task_id;
        console.log('[AI权重异步] 任务已创建:', taskId);

        // ========== 第三步：轮询获取结果 ==========
        // 最多轮询 60 次，每次间隔 5 秒，总计最多 5 分钟
        const maxPolls = 60;
        const pollInterval = 5000; // 5 秒
        let pollCount = 0;
        let notFoundCount = 0; // 连续 404 计数，防止误判瞬时竞态

        // 定义轮询函数，返回 Promise
        const pollResult = () => {
          return new Promise((resolve, reject) => {
            // 递归轮询函数
            const doPoll = async () => {
              pollCount++;
              // 超过最大轮询次数，认为超时
              if (pollCount > maxPolls) {
                reject(new Error('AI权重分析超时，请稍后重试'));
                return;
              }

              try {
                // GET /api/v2/query/ai/analyze_result?task_id=xxx
                const resp = await apiClient.get('v2/query/ai/analyze_result', {
                  params: { task_id: taskId }
                });

                // 解析响应
                let respData = resp;
                if (resp && resp.data) {
                  respData = resp.data;
                }
                console.log('[AI权重异步] 轮询 #' + pollCount + ', 响应:', respData);

                // 404 表示任务不存在或已过期
                if (!respData || respData.code === 404) {
                  notFoundCount++;
                  console.warn('[AI权重异步] 轮询 #' + pollCount + ', 404 not_found (连续' + notFoundCount + '次)');
                  // 连续 3 次 404 才认为任务真的丢失（容忍瞬时竞态）
                  if (notFoundCount >= 3) {
                    reject(new Error('AI权重分析任务已过期，请重新发起'));
                    return;
                  }
                  // 短暂等待后重试（可能后台正在原子写入）
                  setTimeout(doPoll, 2000);
                  return;
                }
                // 恢复正常，重置 404 计数
                notFoundCount = 0;

                // 读取任务状态
                const status = respData.data && respData.data.status;
                console.log('[AI权重异步] 轮询 #' + pollCount + ', 状态:', status);

                // 已完成：把结果返回给外层
                if (status === 'completed') {
                  resolve(respData.data);
                  return;
                }
                // 失败：抛出错误，外层弹窗提示
                if (status === 'failed') {
                  reject(new Error(respData.msg || 'AI权重分析失败'));
                  return;
                }
                // status === 'processing'，继续等待下一轮
                setTimeout(doPoll, pollInterval);
              } catch (err) {
                // 网络错误时继续重试
                console.warn('[AI权重异步] 轮询网络错误:', err);
                setTimeout(doPoll, pollInterval);
              }
            };
            // 启动第一次轮询
            doPoll();
          });
        };

        // 等待轮询结果
        const result = await pollResult();

        // 分析完成：停止进度条动画并跳到 100%
        clearInterval(progressTimer);
        this.aiWeightProgress = 100;

        // ========== 第四步：将 AI 分析结果写入 weightResult ==========
        this.updateWeightResultWithAiData(result);
        // 标记分析完成
        this.aiWeightAnalyzed = true;

        // 提示用户分析完成
        this.$Message.success('AI 账号权重分析完成');
      } catch (error) {
        // 任何异常（创建失败、超时、失败状态）都进入这里
        clearInterval(progressTimer);
        console.error('[AI权重异步] 失败:', error);
        // 显示错误弹窗
        this.showAiWeightErrorDialog('AI分析提示', error.message || 'AI权重分析失败，请稍后重试');
      } finally {
        // 确保进度条定时器一定被清理，并结束分析状态
        clearInterval(progressTimer);
        this.aiWeightAnalyzing = false;
      }
    },

    /**
     * 将 AI 分析结果合并到当前 weightResult 对象
     * 同时触发 Vue 响应式更新，让 UI 自动刷新
     *
     * @param {Object} data - 轮询接口返回的 data 对象（包含 5 个 AI 字段）
     */
    updateWeightResultWithAiData(data) {
      // 参数校验
      if (!data || !this.weightResult) return;
      // 【Vue 3 写法】data 返回的对象已被 reactive() 包装，直接赋值即响应式
      // Vue 2 的 this.$set 在 Vue 3 中不存在（会报 "this.$set is not a function"）
      // 5 个 AI 字段分别写入
      this.weightResult.weight_analysis    = data.weight_analysis || '';
      this.weightResult.growth_suggestions = data.growth_suggestions || '';
      this.weightResult.content_strategy   = data.content_strategy || '';
      this.weightResult.risk_assessment    = data.risk_assessment || '';
      this.weightResult.optimization_plan  = data.optimization_plan || '';
      // 标记已完成 AI 分析
      this.weightResult.isAiWeightAnalyzed = true;
    },

    /**
     * 显示 AI 权重分析错误弹窗
     *
     * @param {string} title - 弹窗标题
     * @param {string} content - 弹窗内容
     */
    showAiWeightErrorDialog(title, content) {
      this.aiWeightErrorDialogTitle = title;
      this.aiWeightErrorDialogContent = content;
      this.aiWeightErrorDialogVisible = true;
    },

    /**
     * 关闭 AI 权重分析错误弹窗
     */
    closeAiWeightErrorDialog() {
      this.aiWeightErrorDialogVisible = false;
    }
  }
};
</script>

<style scoped>
/* ==================== 顶部工具栏样式 ==================== */
/* 工具栏容器：使用Flexbox实现左右布局，包含标题和右侧操作区 */
.toolbar {
  display: flex;                    /* Flexbox布局 */
  justify-content: space-between;   /* 两端对齐 */
  align-items: center;              /* 垂直居中 */
  padding: 15px 20px;               /* 内边距 */
  margin-bottom: 20px;              /* 底部外边距 */
  background: #fff;                 /* 白色背景 */
  border-radius: 8px;               /* 圆角 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);  /* 轻微阴影效果 */
}

/* 页面标题样式 */
.toolbar-left .page-title {
  margin: 0;                        /* 移除默认外边距 */
  font-size: 20px;                  /* 字体大小 */
  font-weight: 600;                 /* 字重加粗 */
  color: #333;                      /* 深灰色文字 */
}

/* 工具栏右侧区域（已移除皮肤选择器） */
.toolbar-right {
  display: flex;                    /* Flexbox布局 */
  align-items: center;              /* 垂直居中 */
  gap: 15px;                        /* 子元素间距 */
}

/* 主题切换器容器（已废弃） */
.theme-switcher {
  display: flex;
  align-items: center;
}

/* 主容器样式：整个查询页面的根容器 */
.weight-query {
  padding: 20px;                    /* 内边距 */
  min-height: 100%;                 /* 最小高度100% */
  background: transparent !important;  /* 透明背景，让主题背景生效 */
  transition: all 0.3s ease;        /* 平滑过渡动画 */
}

/* ==================== 输入区域样式 ==================== */
/* 输入框容器：包含输入框和查询按钮 */
.input-section {
  display: flex;                    /* Flexbox水平布局 */
  gap: 10px;                        /* 子元素间距 */
  margin-bottom: 20px;              /* 底部外边距 */
  padding: 20px;                    /* 内边距 */
  background: #fff;                 /* 白色背景 */
  border-radius: 8px;               /* 圆角 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);  /* 阴影效果 */
}

/* 输入框样式 */
.input-field {
  flex: 1;                          /* 占据剩余空间 */
  padding: 10px 15px;               /* 内边距 */
  border: 1px solid #d9d9d9;       /* 浅灰色边框 */
  border-radius: 4px;               /* 小圆角 */
  font-size: 14px;                  /* 字体大小 */
  transition: all 0.3s ease;        /* 平滑过渡 */
}

/* 输入框聚焦状态：蓝色高亮 */
.input-field:focus {
  border-color: #1890ff;            /* 蓝色边框 */
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);  /* 蓝色光晕 */
  outline: none;                    /* 移除默认轮廓 */
}

/* ==================== 余额信息区域 ==================== */
/* VIP会员信息和积分点数显示容器 */
.balance-info {
  margin-bottom: 20px;              /* 底部外边距 */
  padding: 15px 20px;               /* 内边距 */
  background: #fff;                 /* 白色背景 */
  border-radius: 8px;               /* 圆角 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);  /* 阴影 */
  display: flex;                    /* Flexbox布局 */
  align-items: center;              /* 垂直居中 */
  gap: 10px;                        /* 子元素间距 */
}

/* VIP会员信息样式 */
.vip-info {
  display: flex;                    /* Flexbox布局 */
  align-items: center;              /* 垂直居中 */
  gap: 8px;                         /* 图标和文字间距 */
  color: #ff6b6b;                   /* 红色文字 */
  font-weight: 500;                 /* 中等字重 */
}

/* 剩余天数显示 */
.days-left {
  color: #ff964b;                   /* 橙色文字 */
  font-size: 13px;                  /* 较小字体 */
  margin-left: 5px;                 /* 左侧间距 */
}

/* 积分点数信息 */
.points-info {
  display: flex;                    /* Flexbox布局 */
  align-items: center;              /* 垂直居中 */
  gap: 15px;                        /* 子元素间距 */
  color: #333;                      /* 深灰色文字 */
}

/* ==================== 查询结果区域 ==================== */
/* 权重查询结果显示容器 */
.weight-result {
  margin-bottom: 20px;              /* 底部外边距 */
  padding: 20px;                    /* 内边距 */
  background: #fff;                 /* 白色背景 */
  border-radius: 8px;               /* 圆角 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);  /* 阴影效果 */
}

/* 账号信息区域：头像、昵称、ID等 */
.account-info {
  display: flex;                    /* Flexbox布局 */
  align-items: center;              /* 垂直居中 */
  gap: 20px;                        /* 子元素间距 */
  padding-bottom: 20px;             /* 底部内边距 */
  border-bottom: 1px solid #eee;   /* 底部分隔线 */
  margin-bottom: 20px;              /* 底部外边距 */
}

/* 用户详细信息（昵称、ID、描述） */
.user-details h3 {
  margin: 0 0 8px;                  /* 上右下左外边距 */
  color: #333;                      /* 深灰色文字 */
  font-size: 20px;                  /* 字体大小 */
}

/* 账号ID显示 */
.account-id {
  margin: 0 0 5px;                  /* 底部外边距 */
  color: #666;                      /* 中灰色文字 */
  font-size: 14px;                  /* 字体大小 */
}

/* 账号描述文本 */
.description {
  margin: 0;                        /* 移除默认外边距 */
  color: #999;                      /* 浅灰色文字 */
  font-size: 13px;                  /* 较小字体 */
}

/* ==================== 核心指标卡片 ==================== */
/* 4列网格布局：显示权重分、粉丝数、点赞数、作品数 */
.core-metrics {
  display: grid;                    /* Grid网格布局 */
  grid-template-columns: repeat(4, 1fr);  /* 4列等宽 */
  gap: 15px;                        /* 网格间距 */
  margin-bottom: 30px;              /* 底部外边距 */
}

/* 单个指标卡片样式 */
.metric-card {
  text-align: center;               /* 文字居中 */
  padding: 20px;                    /* 内边距 */
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);  /* 紫色渐变背景 */
  border-radius: 8px;               /* 圆角 */
  color: #fff;                      /* 白色文字 */
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);  /* 彩色阴影 */
  transition: transform 0.3s ease, box-shadow 0.3s ease;  /* 平滑过渡 */
}

/* 卡片悬浮效果：向上移动+阴影增强 */
.metric-card:hover {
  transform: translateY(-4px);      /* 向上移动4px */
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);  /* 阴影加深 */
}

/* 指标数值：大字体加粗 */
.metric-value {
  font-size: 32px;                  /* 超大字体 */
  font-weight: bold;                /* 加粗 */
  margin-bottom: 8px;               /* 底部外边距 */
}

/* 指标标签：说明文字 */
.metric-label {
  font-size: 14px;                  /* 字体大小 */
  opacity: 0.9;                     /* 轻微透明 */
}

/* ==================== 数据表格区域 ==================== */
/* 详细数据表格容器（作品列表、粉丝画像等） */
.data-table-section {
  margin-top: 20px;                 /* 顶部外边距 */
}

/* 自定义斑马线效果 - 替代View UI Plus的stripe属性 */
/* 偶数行背景色 */
.custom-stripe-table /deep/ .ivu-table-body tr:nth-child(2n) td {
  background: #fafafa;              /* 浅灰色背景 */
}

/* 悬浮行背景色（优先级更高） */
.custom-stripe-table /deep/ .ivu-table-body tr.ivu-table-row-hover td {
  background: #e6f7ff !important;   /* 浅蓝色背景 */
}

/* 偶数行悬浮时的背景色 */
.custom-stripe-table /deep/ .ivu-table-body tr:nth-child(2n).ivu-table-row-hover td {
  background: #bae7ff !important;   /* 稍深蓝色 */
}

/* 经典模式下表格边框增强 */
.theme-default .custom-stripe-table /deep/ .ivu-table {
  border: 1px solid #e8e8e8;       /* 灰色边框 */
}

/* 经典模式表头样式 */
.theme-default .custom-stripe-table /deep/ .ivu-table th {
  background: #fafafa !important;   /* 浅灰背景 */
  font-weight: 600;                 /* 加粗 */
  color: #333;                      /* 深灰文字 */
}

/* 表格头部：标题和分页控件 */
.table-header {
  display: flex;                    /* Flexbox布局 */
  justify-content: space-between;   /* 两端对齐 */
  align-items: center;              /* 垂直居中 */
  margin-bottom: 15px;              /* 底部外边距 */
}

/* 表格标题 */
.table-header h3 {
  margin: 0;                        /* 移除默认外边距 */
  color: #333;                      /* 深灰文字 */
}

/* 分页控件容器 */
.pagination {
  display: flex;                    /* Flexbox布局 */
  align-items: center;              /* 垂直居中 */
  gap: 10px;                        /* 子元素间距 */
}

/* 分页信息文字 */
.page-info {
  color: #666;                      /* 中灰文字 */
  font-size: 14px;                  /* 字体大小 */
}

/* ==================== 图片单元格样式 ==================== */
/* 图片单元格容器：居中显示 */
.image-cell {
  display: flex;                    /* Flexbox布局 */
  justify-content: center;          /* 水平居中 */
  align-items: center;              /* 垂直居中 */
}

/* 等级图片样式 */
.level-image {
  max-width: 80px;                  /* 最大宽度 */
  max-height: 40px;                 /* 最大高度 */
}

/* ==================== 图片网格布局 ==================== */
/* 自适应网格：自动填充，最小120px，最大1fr */
.image-grid {
  display: grid;                    /* Grid网格布局 */
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));  /* 自适应列 */
  gap: 10px;                        /* 网格间距 */
  padding: 10px;                    /* 内边距 */
}

/* 平板端响应式：减小最小宽度 */
@media (max-width: 768px) {
  .image-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
}

/* 手机端响应式：固定2列布局 */
@media (max-width: 480px) {
  .image-grid {
    grid-template-columns: repeat(2, 1fr);  /* 2列等宽 */
  }
}

/* 单个图片项容器 */
.image-item {
  text-align: center;               /* 文字居中 */
}

/* 网格图片样式 */
.grid-image {
  width: 100%;                      /* 全宽 */
  height: 100px;                    /* 固定高度 */
  object-fit: cover;                /* 裁剪填充 */
  border-radius: 4px;               /* 小圆角 */
  margin-bottom: 5px;               /* 底部外边距 */
  transition: transform 0.3s;       /* 平滑过渡 */
}

/* 图片悬浮效果：放大 */
.grid-image:hover {
  transform: scale(1.05);           /* 放大5% */
}

/* 图片名称文字 */
.image-name {
  font-size: 12px;                  /* 小字体 */
  color: #666;                      /* 中灰文字 */
  margin: 0;                        /* 移除默认外边距 */
  line-height: 1.4;                 /* 行高 */
  font-weight: 500;                 /* 中等字重 */
}

/* ==================== 图片统计数据 ==================== */
/* 统计信息容器：点赞、评论、分享数 */
.image-stats {
  display: flex;                    /* Flexbox布局 */
  justify-content: space-around;    /* 均匀分布 */
  align-items: center;              /* 垂直居中 */
  padding: 6px 4px;                 /* 内边距 */
  background: #f8f9fa;              /* 浅灰背景 */
  border-radius: 4px;               /* 小圆角 */
  margin-top: 5px;                  /* 顶部外边距 */
  font-size: 11px;                  /* 超小字体 */
}

/* 单个统计项 */
.stat-item {
  display: flex;                    /* Flexbox布局 */
  align-items: center;              /* 垂直居中 */
  gap: 3px;                         /* 图标和数字间距 */
  color: #666;                      /* 中灰文字 */
  cursor: default;                  /* 默认鼠标指针 */
}

/* 统计项图标 */
.stat-item .ivu-icon {
  color: #999;                      /* 浅灰图标 */
}

/* 图片提示文字 */
.image-tips {
  font-size: 12px;                  /* 小字体 */
  color: #666;                      /* 中灰文字 */
  margin: 0;                        /* 移除默认外边距 */
  line-height: 1.4;                 /* 行高 */
}

/* ==================== 粉丝画像区域 ==================== */
/* 粉丝信息容器：性别分布、年龄分布等 */
.fans-profile {
  display: flex;                    /* Flexbox布局 */
  flex-direction: column;           /* 垂直排列 */
  gap: 10px;                        /* 子元素间距 */
  padding: 10px;                    /* 内边距 */
}

/* 单个粉丝信息项 */
.fans-item {
  padding: 12px;                    /* 内边距 */
  background: #f8f9fa;              /* 浅灰背景 */
  border-radius: 6px;               /* 圆角 */
  border-left: 3px solid #1890ff;   /* 左侧蓝色边框 */
}

/* 粉丝信息标题 */
.fans-item-header {
  font-weight: 600;                 /* 加粗 */
  color: #333;                      /* 深灰文字 */
  margin-bottom: 8px;               /* 底部外边距 */
  font-size: 14px;                  /* 字体大小 */
}

/* 粉丝信息内容 */
.fans-item-content {
  color: #666;                      /* 中灰文字 */
  font-size: 13px;                  /* 较小字体 */
  line-height: 1.6;                 /* 行高 */
}

/* 粉丝图表容器 */
.fans-chart {
  width: 100%;                      /* 全宽 */
  max-width: 400px;                 /* 最大宽度 */
  margin-top: 10px;                 /* 顶部外边距 */
}

/* ==================== 封面图区域 ==================== */
/* 封面图片网格：自适应列 */
.cover-images {
  display: grid;                    /* Grid网格布局 */
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));  /* 最小150px */
  gap: 10px;                        /* 网格间距 */
}

/* 单个封面项 */
.cover-item {
  text-align: center;               /* 文字居中 */
}

/* 封面图片样式 */
.cover-image {
  width: 100%;                      /* 全宽 */
  height: 200px;                    /* 固定高度 */
  object-fit: cover;                /* 裁剪填充 */
  border-radius: 4px;               /* 小圆角 */
  margin-bottom: 5px;               /* 底部外边距 */
}

/* 封面提示文字 */
.cover-tips {
  font-size: 12px;                  /* 小字体 */
  color: #666;                      /* 中灰文字 */
  margin: 0;                        /* 移除默认外边距 */
}

/* ==================== 查询历史记录区域 ==================== */
/* 历史记录容器：显示之前的查询结果 */
.history-section {
  padding: 20px;                    /* 内边距 */
  background: #fff;                 /* 白色背景 */
  border-radius: 8px;               /* 圆角 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);  /* 阴影效果 */
}

/* 历史记录头部：标题和清空按钮 */
.history-header {
  display: flex;                    /* Flexbox布局 */
  justify-content: space-between;   /* 两端对齐 */
  align-items: center;              /* 垂直居中 */
  margin-bottom: 15px;              /* 底部外边距 */
}

/* 历史记录标题 */
.history-header h3 {
  margin: 0;                        /* 移除默认外边距 */
  color: #333;                      /* 深灰文字 */
}

/* 昵称文字样式 */
.nickname-text,
.time-text {
  font-size: 13px;                  /* 较小字体 */
}

/* 权重分数样式：蓝色加粗 */
.score-text {
  font-weight: bold;                /* 加粗 */
  color: #1890ff;                   /* 蓝色文字 */
}

/* 空历史记录提示 */
.empty-history {
  text-align: center;               /* 文字居中 */
  padding: 40px 0;                  /* 上下内边距 */
  color: #999;                      /* 浅灰文字 */
}

/* 历史分页控件 */
.history-pagination {
  margin-top: 20px;                 /* 顶部外边距 */
  display: flex;                    /* Flexbox布局 */
  justify-content: space-between;   /* 两端对齐 */
  align-items: center;              /* 垂直居中 */
}

/* 分页按钮组 */
.page-buttons {
  display: flex;                    /* Flexbox布局 */
  gap: 10px;                        /* 按钮间距 */
}

/* ==================== Loading加载遮罩 ==================== */
/* 全屏遮罩层：查询时显示 */
.loading-overlay {
  position: fixed;                  /* 固定定位 */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);  /* 半透明白色背景 */
  display: flex;                    /* Flexbox布局 */
  align-items: center;              /* 垂直居中 */
  justify-content: center;          /* 水平居中 */
  z-index: 1000;                    /* 最高层级，覆盖所有元素 */
}

/* Loading旋转图标容器 */
.loading-spinner {
  text-align: center;               /* 文字居中 */
}

/* 旋转图标动画 */
.spin-icon {
  animation: spin 1s linear infinite;  /* 1秒匀速无限旋转 */
  color: #1890ff;                   /* 蓝色图标 */
}

/* 定义旋转动画关键帧 */
@keyframes spin {
  from { transform: rotate(0deg); }    /* 起始角度 */
  to { transform: rotate(360deg); }    /* 结束角度 */
}

/* ==================== 响应式设计 - 平板端（≤768px）==================== */
@media (max-width: 768px) {
  /* 主容器：减小内边距，增加顶部间距避免被导航栏遮挡 */
  .weight-query {
    padding: 10px !important;       /* 减小内边距 */
    padding-top: 20px !important;   /* 手机端增加顶部间距，避免被导航栏遮挡 */
  }

  /* 工具栏：改为垂直布局，标题居中 */
  .toolbar {
    flex-direction: column;         /* 垂直排列 */
    gap: 10px;                      /* 子元素间距 */
    padding: 10px;                  /* 减小内边距 */
    position: relative;             /* 相对定位 */
  }

  /* 页面标题：缩小字体，居中对齐 */
  .toolbar-left .page-title {
    font-size: 18px;                /* 缩小字体 */
    text-align: center;             /* 居中显示 */
    margin-bottom: 8px;             /* 底部外边距 */
  }

  /* 工具栏右侧：全宽居中 */
  .toolbar-right {
    width: 100%;                    /* 全宽 */
    display: flex;
    justify-content: center;        /* 水平居中 */
  }

  /* ==================== 输入区域移动端优化 ==================== */
  /* 输入框容器：改为垂直布局，按钮全宽 */
  .input-section.mobile-input {
    flex-direction: column;         /* 垂直排列 */
    padding: 12px;                  /* 减小内边距 */
    gap: 10px;                      /* 子元素间距 */
  }

  /* 输入框：全宽度，增大触摸区域 */
  .input-section.mobile-input .input-field {
    width: 100%;                    /* 全宽 */
    font-size: 14px;                /* 字体大小 */
    padding: 10px 12px;             /* 内边距 */
  }

  /* 查询按钮：全宽度，增大高度便于点击 */
  .input-section.mobile-input .ivu-btn {
    width: 100%;                    /* 全宽 */
    height: 44px;                   /* 增大高度（适合手指点击） */
    font-size: 16px;                /* 字体大小 */
  }

  /* ==================== VIP/点数信息移动端优化 ==================== */
  /* 余额信息容器：减小内边距 */
  .balance-info.mobile-balance {
    padding: 12px;                  /* 减小内边距 */
  }

  /* VIP信息和积分信息：改为垂直堆叠 */
  .balance-info.mobile-balance .vip-info,
  .balance-info.mobile-balance .points-info {
    flex-direction: column;         /* 垂直排列 */
    align-items: flex-start;        /* 左对齐 */
    gap: 8px;                       /* 子元素间距 */
  }

  /* 文字信息：全宽显示 */
  .balance-info.mobile-balance .vip-info span,
  .balance-info.mobile-balance .points-info span {
    font-size: 13px;                /* 较小字体 */
    width: 100%;                    /* 全宽 */
  }

  /* 充值按钮：全宽度 */
  .balance-info.mobile-balance .ivu-btn {
    width: 100%;                    /* 全宽 */
    margin-top: 8px;                /* 顶部外边距 */
  }

  /* ==================== 核心指标卡片移动端优化 ==================== */
  /* 改为2列布局（原来是4列） */
  .core-metrics {
    grid-template-columns: repeat(2, 1fr);  /* 2列等宽 */
    gap: 10px;                      /* 减小间距 */
  }

  /* 指标卡片：减小内边距 */
  .metric-card {
    padding: 15px 10px;             /* 减小内边距 */
  }

  /* 指标数值：缩小字体 */
  .metric-value {
    font-size: 24px;                /* 从32px缩小到24px */
  }

  /* 指标标签：缩小字体 */
  .metric-label {
    font-size: 12px;                /* 从14px缩小到12px */
  }

  /* ==================== 历史记录移动端优化 ==================== */
  /* 历史记录容器：减小内边距，允许横向滚动 */
  .history-section.mobile-history {
    padding: 12px;                  /* 减小内边距 */
    overflow-x: auto;               /* 允许横向滚动 */
  }

  /* 历史记录头部：垂直布局 */
  .history-header {
    flex-direction: column;         /* 垂直排列 */
    gap: 10px;                      /* 子元素间距 */
    align-items: stretch;           /* 拉伸对齐 */
  }

  /* 历史记录标题：居中显示 */
  .history-header h3 {
    font-size: 16px;                /* 缩小字体 */
    text-align: center;             /* 居中显示 */
  }

  /* 清空按钮：全宽度 */
  .history-header .ivu-btn {
    width: 100%;                    /* 全宽 */
  }

  /* ==================== 操作按钮移动端优化 ==================== */
  /* 操作按钮组：垂直堆叠，全宽度 */
  .action-btn-group {
    display: flex;
    flex-direction: column;         /* 垂直排列 */
    gap: 6px;                       /* 按钮间距 */
  }

  /* 单个操作按钮：全宽度，增大高度 */
  .action-btn-group .ivu-btn {
    width: 100%;                    /* 全宽 */
    margin: 0 !important;           /* 移除默认外边距 */
    font-size: 13px;                /* 较小字体 */
    height: 36px;                   /* 固定高度 */
  }

  /* 历史分页：垂直布局 */
  .history-pagination {
    flex-direction: column;         /* 垂直排列 */
    gap: 15px;                      /* 子元素间距 */
  }

  /* ==================== 弹窗内容移动端优化 ==================== */
  /* 视频数据分析弹窗：改为2列布局 */
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);  /* 2列等宽 */
  }

  /* 详情弹窗信息网格：单列布局 */
  .info-grid {
    grid-template-columns: 1fr;     /* 单列 */
  }

  /* 详情弹窗指标网格：单列布局 */
  .metrics-grid {
    grid-template-columns: 1fr;     /* 单列 */
  }
}

/* ==================== 响应式设计 - 手机端（≤480px）==================== */
@media (max-width: 480px) {
  /* 核心指标：单列布局 */
  .core-metrics {
    grid-template-columns: 1fr;     /* 单列 */
  }

  /* 统计数据网格：单列布局 */
  .stats-grid {
    grid-template-columns: 1fr;     /* 单列 */
  }

  /* 页面标题：进一步缩小 */
  .page-title {
    font-size: 16px !important;     /* 更小字体 */
  }

  /* 输入框：缩小字体 */
  .input-section.mobile-input .input-field {
    font-size: 13px;                /* 较小字体 */
  }

  /* 指标数值：进一步缩小 */
  .metric-value {
    font-size: 20px;                /* 从24px缩小到20px */
  }
}

/* ==================== 主题样式系统 ==================== */

/* ==================== 经典主题（默认浅色主题）==================== */
/* 主容器：透明背景，让白色背景生效 */
.theme-default.weight-query {
  background: transparent !important;
}

/* 工具栏：白色背景，深色边框 */
.theme-default .toolbar {
  background: #ffffff;              /* 纯白背景 */
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);  /* 较深阴影 */
  border: 2px solid #b0b0b0;       /* 灰色边框 */
}

/* 页面标题：深色文字，加粗 */
.theme-default .page-title {
  color: #1a1a1a;                   /* 接近黑色的深灰 */
  font-weight: 700;                 /* 加粗 */
}

/* 所有卡片容器：白色背景，深色边框 */
.theme-default .input-section,
.theme-default .balance-info,
.theme-default .weight-result,
.theme-default .history-section {
  background: #ffffff;              /* 纯白背景 */
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);  /* 较深阴影 */
  border: 2px solid #b0b0b0;       /* 灰色边框 */
}

/* ==================== 经典主题文字颜色强制加深 ==================== */
/* 工具栏内所有元素：深色文字 */
.theme-default .toolbar * {
  color: #1a1a1a !important;        /* 强制深色文字 */
}

/* 输入区域内所有元素：深色文字 */
.theme-default .input-section * {
  color: #1a1a1a !important;
}

/* 余额信息内所有元素：深色文字 */
.theme-default .balance-info * {
  color: #1a1a1a !important;
}

/* 查询结果内所有元素：深色文字 */
.theme-default .weight-result * {
  color: #1a1a1a !important;
}

/* 历史记录内所有元素：深色文字 */
.theme-default .history-section * {
  color: #1a1a1a !important;
}

/* ==================== 经典主题输入框样式 ==================== */
/* 输入框：浅灰背景，深色边框 */
.theme-default .input-field {
  background: #f9f9f9 !important;   /* 浅灰背景 */
  border: 2px solid #b0b0b0 !important;  /* 灰色边框 */
  color: #1a1a1a !important;        /* 深色文字 */
}

/* 输入框占位符文字 */
.theme-default .input-field::placeholder {
  color: #999 !important;           /* 浅灰占位符 */
}

/* ==================== 经典主题按钮样式 ==================== */
/* 按钮文字：深色 */
.theme-default .ivu-btn {
  color: #1a1a1a !important;        /* 深色文字 */
}

/* 按钮内部span元素：深色 */
.theme-default .ivu-btn span {
  color: #1a1a1a !important;
}

/* ==================== 星空主题（深蓝色渐变 + 星星效果）==================== */
/* 主容器：深蓝渐变背景 */
.theme-starry.weight-query {
  background: linear-gradient(135deg, #0c1445 0%, #1a237e 50%, #0d47a1 100%);  /* 深蓝渐变 */
  position: relative;               /* 相对定位，为伪元素做准备 */
}

/* 星空背景效果：使用伪元素创建星星 */
.theme-starry.weight-query::before {
  content: '';                      /* 空内容 */
  position: absolute;               /* 绝对定位 */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* 使用多个径向渐变创建星星点点的效果 */
  background-image: 
    radial-gradient(2px 2px at 20px 30px, #fff, transparent),
    radial-gradient(2px 2px at 40px 70px, #fff, transparent),
    radial-gradient(1px 1px at 90px 40px, #fff, transparent),
    radial-gradient(2px 2px at 160px 120px, #fff, transparent),
    radial-gradient(1px 1px at 200px 50px, #fff, transparent),
    radial-gradient(2px 2px at 250px 180px, #fff, transparent),
    radial-gradient(1px 1px at 320px 90px, #fff, transparent),
    radial-gradient(2px 2px at 380px 220px, #fff, transparent),
    radial-gradient(1px 1px at 450px 60px, #fff, transparent),
    radial-gradient(2px 2px at 500px 150px, #fff, transparent);
  background-size: 550px 250px;     /* 背景图案大小 */
  opacity: 0.3;                     /* 30%透明度 */
  pointer-events: none;             /* 不响应鼠标事件 */
}

/* ==================== 星空主题卡片样式（毛玻璃效果）==================== */
/* 工具栏：半透明背景 + 毛玻璃效果 */
.theme-starry .toolbar {
  background: rgba(255, 255, 255, 0.1);  /* 10%透明白色 */
  backdrop-filter: blur(10px);      /* 毛玻璃模糊效果 */
  border: 1px solid rgba(255, 255, 255, 0.2);  /* 半透明白色边框 */
}

/* 页面标题：白色文字 */
.theme-starry .page-title {
  color: #fff;                      /* 纯白文字 */
}

/* 所有卡片容器：半透明背景 + 毛玻璃效果 */
.theme-starry .input-section,
.theme-starry .balance-info,
.theme-starry .weight-result,
.theme-starry .history-section {
  background: rgba(255, 255, 255, 0.1);  /* 10%透明白色 */
  backdrop-filter: blur(10px);      /* 毛玻璃模糊 */
  border: 1px solid rgba(255, 255, 255, 0.2);  /* 半透明白色边框 */
  color: #fff;                      /* 白色文字 */
}

/* 星空主题下，点数信息卡片内的文字必须显式继承白色
   否则子元素 span 默认色会让"当前点数"、"剩余次数"看不清 */
.theme-starry .balance-info,
.theme-starry .balance-info span,
.theme-starry .balance-info .points-info,
.theme-starry .balance-info .points-info span,
.theme-starry .balance-info .vip-info,
.theme-starry .balance-info .vip-info span,
.theme-starry .balance-info .query-cost,
.theme-starry .balance-info .query-cost span,
.theme-starry .balance-info .remaining-queries,
.theme-starry .balance-info .days-left {
  color: #ffffff !important;        /* 强制白色，对比深色半透明背景更清晰 */
  font-weight: 600;                 /* 略加粗进一步提升可读性 */
}

/* ==================== 星空主题输入框样式 ==================== */
/* 输入框：半透明背景，白色文字 */
.theme-starry .input-field {
  background: rgba(255, 255, 255, 0.1);  /* 10%透明白色 */
  border: 1px solid rgba(255, 255, 255, 0.3);  /* 30%透明白色边框 */
  color: #fff;                      /* 白色文字 */
}

/* 输入框占位符：半透明白色 */
.theme-starry .input-field::placeholder {
  color: rgba(255, 255, 255, 0.6);  /* 60%透明白色 */
}

/* ==================== 星空主题用户信息样式 ==================== */
/* 用户昵称：白色 */
.theme-starry .user-details h3 {
  color: #fff;                      /* 白色文字 */
}

/* 账号ID：半透明白色 */
.theme-starry .account-id {
  color: rgba(255, 255, 255, 0.8);  /* 80%透明白色 */
}

/* 账号描述：更低透明度 */
.theme-starry .description {
  color: rgba(255, 255, 255, 0.6);  /* 60%透明白色 */
}

/* ==================== 星空主题表格标题样式 ==================== */
/* 表格标题：白色文字 */
.theme-starry .table-header h3,
.theme-starry .history-header h3 {
  color: #fff;                      /* 白色文字 */
}

/* 分页信息：白色 */
.theme-starry .page-info {
  color: #fff !important;           /* 强制白色 */
}

/* 昵称和时间文字：白色 */
.theme-starry .nickname-text,
.theme-starry .time-text {
  color: #fff !important;
}

/* ==================== 星空主题表格样式（强制白色文字）==================== */
/* 表格整体：白色文字 */
.theme-starry .ivu-table {
  color: #fff !important;           /* 强制白色文字 */
}

/* 表头：深色半透明背景，白色文字 */
.theme-starry .ivu-table th,
.theme-starry .ivu-table thead th {
  color: #fff !important;
  background: rgba(0, 0, 0, 0.3) !important;  /* 30%透明黑色背景 */
}

/* 表格单元格：白色文字 */
.theme-starry .ivu-table td,
.theme-starry .ivu-table tbody td {
  color: #fff !important;
}

/* 表头单元格：白色文字 */
.theme-starry .ivu-table-header th {
  color: #fff !important;
}

/* 确保所有表格内容都是白色 */
.theme-starry .ivu-table-cell {
  color: #fff !important;
}

/* 表格主体单元格：白色文字 */
.theme-starry .ivu-table-body td {
  color: #fff !important;
}

/* ==================== 星空主题操作按钮组样式 ==================== */
/* 操作按钮组基础样式 */
.action-btn-group {
  display: flex;                    /* Flexbox布局 */
  gap: 8px;                         /* 按钮间距 */
  align-items: center;              /* 垂直居中 */
}

/* 星空主题下操作按钮间距优化 */
.theme-starry .action-btn-group {
  gap: 6px;                         /* 减小间距 */
}

/* 星空主题下按钮外边距重置 */
.theme-starry .action-btn-group .ivu-btn {
  margin: 0 !important;             /* 移除默认外边距 */
}

/* ==================== 星空主题移动端优化（≤768px）==================== */
@media (max-width: 768px) {
  /* 主容器：减小内边距 */
  .theme-starry.weight-query {
    padding: 8px;                   /* 更小内边距 */
  }

  /* 工具栏：减小内边距 */
  .theme-starry .toolbar {
    padding: 8px;
  }

  /* 所有卡片容器：减小内边距 */
  .theme-starry .input-section,
  .theme-starry .balance-info,
  .theme-starry .weight-result,
  .theme-starry .history-section {
    padding: 12px;                  /* 减小内边距 */
  }

  /* 历史记录头部：减小底部外边距 */
  .theme-starry .history-header {
    margin-bottom: 10px;
  }

  /* ==================== 星空主题移动端表格文字优化 ==================== */
  /* 表格单元格：缩小字体 */
  .theme-starry .ivu-table td,
  .theme-starry .ivu-table th {
    font-size: 13px;                /* 较小字体 */
  }

  /* 昵称、时间、分数文字：进一步缩小 */
  .theme-starry .nickname-text,
  .theme-starry .time-text,
  .theme-starry .score-text {
    font-size: 12px;                /* 更小字体 */
  }
}

/* 星空主题空记录提示：半透明白色 */
.theme-starry .empty-history {
  color: rgba(255, 255, 255, 0.6);  /* 60%透明白色 */
}

/* ==================== 星空主题详细数据表格样式 ==================== */
/* 默认白色背景（非星空主题） */
.weight-result .data-table-section {
  background: #fff;                 /* 白色背景 */
  border-radius: 8px;               /* 圆角 */
  padding: 20px;                    /* 内边距 */
  margin-top: 20px;                 /* 顶部外边距 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);  /* 阴影 */
  border: 1px solid #d0d0d0;       /* 灰色边框 */
}

/* 星空主题 - 详细数据表格使用半透明背景 */
.theme-starry .weight-result .data-table-section {
  background: rgba(255, 255, 255, 0.1) !important;  /* 10%透明白色 */
  backdrop-filter: blur(10px);      /* 毛玻璃效果 */
  border: 1px solid rgba(255, 255, 255, 0.2) !important;  /* 半透明白色边框 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;  /* 较深阴影 */
}

/* 星空主题 - 详细数据表格标题：白色 */
.theme-starry .weight-result .data-table-section .table-header h3 {
  color: #fff !important;           /* 强制白色 */
}

/* 星空主题 - 详细数据表格分页文字：半透明白色 */
.theme-starry .weight-result .data-table-section .page-info {
  color: rgba(255, 255, 255, 0.9) !important;  /* 90%透明白色 */
}

/* ==================== 星空主题详细数据表格内容样式 ==================== */
/* 表格整体：透明背景，白色文字 */
.theme-starry .weight-result .custom-stripe-table /deep/ .ivu-table {
  color: #fff !important;
  background: transparent !important;  /* 透明背景 */
}

/* 表头：深色半透明背景 */
.theme-starry .weight-result .custom-stripe-table /deep/ .ivu-table th {
  background: rgba(0, 0, 0, 0.3) !important;  /* 30%透明黑色 */
  color: #fff !important;
}

/* 表格单元格：透明背景，白色文字 */
.theme-starry .weight-result .custom-stripe-table /deep/ .ivu-table td {
  color: #fff !important;
  background: transparent !important;
}

/* ==================== 星空主题斑马线效果 ==================== */
/* 偶数行：轻微白色背景 */
.theme-starry .weight-result .custom-stripe-table /deep/ .ivu-table-body tr:nth-child(2n) td {
  background: rgba(255, 255, 255, 0.05) !important;  /* 5%透明白色 */
}

/* 悬浮行：更明显的白色背景 */
.theme-starry .weight-result .custom-stripe-table /deep/ .ivu-table-body tr.ivu-table-row-hover td {
  background: rgba(255, 255, 255, 0.15) !important;  /* 15%透明白色 */
}

/* ==================== 其他主题详细数据表格样式（统一白色背景）==================== */
/* 蓝色主题 - 详细数据表格 */
.theme-blue .weight-result .data-table-section {
  background: #fff;                 /* 白色背景 */
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
}

/* 紫色主题 - 详细数据表格 */
.theme-purple .weight-result .data-table-section {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
}

/* 绿色主题 - 详细数据表格 */
.theme-green .weight-result .data-table-section {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
}

/* 红色主题 - 详细数据表格 */
.theme-red .weight-result .data-table-section {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
}

/* 橙色主题 - 详细数据表格 */
.theme-orange .weight-result .data-table-section {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
}
/* ==================== 蓝色主题（浅蓝渐变背景 + 深蓝工具栏）==================== */
.theme-blue.weight-query {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);  /* 浅蓝渐变 */
}

/* 工具栏：深蓝色背景 */
.theme-blue .toolbar {
  background: #1976d2;              /* 深蓝色 */
}

/* 页面标题：白色文字 */
.theme-blue .page-title {
  color: #fff;                      /* 白色文字 */
}

/* 所有卡片容器：白色背景 */
.theme-blue .input-section,
.theme-blue .balance-info,
.theme-blue .weight-result,
.theme-blue .history-section {
  background: #fff;                 /* 白色背景 */
}

/* ==================== 紫色主题（浅紫渐变背景 + 深紫工具栏）==================== */
.theme-purple.weight-query {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);  /* 浅紫渐变 */
}

/* 工具栏：深紫色背景 */
.theme-purple .toolbar {
  background: #7b1fa2;              /* 深紫色 */
}

/* 页面标题：白色文字 */
.theme-purple .page-title {
  color: #fff;                      /* 白色文字 */
}

/* 所有卡片容器：白色背景 */
.theme-purple .input-section,
.theme-purple .balance-info,
.theme-purple .weight-result,
.theme-purple .history-section {
  background: #fff;                 /* 白色背景 */
}

/* ==================== 绿色主题（浅绿渐变背景 + 深绿工具栏）==================== */
.theme-green.weight-query {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);  /* 浅绿渐变 */
}

/* 工具栏：深绿色背景 */
.theme-green .toolbar {
  background: #388e3c;              /* 深绿色 */
}

/* 页面标题：白色文字 */
.theme-green .page-title {
  color: #fff;                      /* 白色文字 */
}

/* 所有卡片容器：白色背景 */
.theme-green .input-section,
.theme-green .balance-info,
.theme-green .weight-result,
.theme-green .history-section {
  background: #fff;                 /* 白色背景 */
}

/* ==================== 红色主题（浅红渐变背景 + 深红工具栏）==================== */
.theme-red.weight-query {
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);  /* 浅红渐变 */
}

/* 工具栏：深红色背景 */
.theme-red .toolbar {
  background: #d32f2f;              /* 深红色 */
}

/* 页面标题：白色文字 */
.theme-red .page-title {
  color: #fff;                      /* 白色文字 */
}

/* 所有卡片容器：白色背景 */
.theme-red .input-section,
.theme-red .balance-info,
.theme-red .weight-result,
.theme-red .history-section {
  background: #fff;                 /* 白色背景 */
}

/* ==================== 橙色主题（浅橙渐变背景 + 深橙工具栏）==================== */
.theme-orange.weight-query {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);  /* 浅橙渐变 */
}

/* 工具栏：深橙色背景 */
.theme-orange .toolbar {
  background: #f57c00;              /* 深橙色 */
}

/* 页面标题：白色文字 */
.theme-orange .page-title {
  color: #fff;                      /* 白色文字 */
}

/* 所有卡片容器：白色背景 */
.theme-orange .input-section,
.theme-orange .balance-info,
.theme-orange .weight-result,
.theme-orange .history-section {
  background: #fff;                 /* 白色背景 */
}

/* ==================== 视频数据分析弹窗样式 ==================== */
/* 弹窗内容容器 */
.video-analysis-content {
  padding: 10px;                    /* 内边距 */
}

/* 分析区块：每个分析部分 */
.analysis-section {
  margin-bottom: 25px;              /* 底部外边距 */
  padding-bottom: 20px;             /* 底部内边距 */
  border-bottom: 1px solid #eee;   /* 底部分隔线 */
}

/* 最后一个区块：移除分隔线 */
.analysis-section:last-child {
  border-bottom: none;
}

/* 区块标题 */
.analysis-section h4 {
  font-size: 16px;                  /* 字体大小 */
  font-weight: 600;                 /* 加粗 */
  color: #333;                      /* 深灰文字 */
  margin: 0 0 15px 0;               /* 上右下左外边距 */
}

/* 封面图片容器：居中显示 */
.cover-image-wrapper {
  text-align: center;               /* 文字居中 */
}

/* 封面图片样式 */
.cover-image {
  max-width: 100%;                  /* 最大全宽 */
  max-height: 400px;                /* 最大高度 */
  border-radius: 8px;               /* 圆角 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);  /* 阴影 */
}

/* ==================== 统计数据网格（3列）==================== */
.stats-grid {
  display: grid;                    /* Grid网格布局 */
  grid-template-columns: repeat(3, 1fr);  /* 3列等宽 */
  gap: 15px;                        /* 网格间距 */
}

/* 单个统计卡片：紫色渐变背景 */
.stat-card {
  padding: 15px;                    /* 内边距 */
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);  /* 紫色渐变 */
  border-radius: 8px;               /* 圆角 */
  color: #fff;                      /* 白色文字 */
  text-align: center;               /* 文字居中 */
}

/* 统计标签：说明文字 */
.stat-label {
  font-size: 13px;                  /* 较小字体 */
  opacity: 0.9;                     /* 轻微透明 */
  margin-bottom: 8px;               /* 底部外边距 */
}

/* 统计数值：大字体加粗 */
.stat-value {
  font-size: 24px;                  /* 大字体 */
  font-weight: bold;                /* 加粗 */
}

/* 话题标签容器：flex布局，自动换行 */
.topics-wrapper {
  display: flex;                    /* Flexbox布局 */
  flex-wrap: wrap;                  /* 允许换行 */
  gap: 8px;                         /* 标签间距 */
}

/* ==================== 限制信息提示框（橙色警告）==================== */
.limit-info {
  padding: 15px;                    /* 内边距 */
  background: #fff3e0;              /* 浅橙色背景 */
  border-radius: 6px;               /* 圆角 */
  border-left: 4px solid #ff9800;   /* 左侧橙色边框 */
}

/* 限制信息段落 */
.limit-info p {
  margin: 0 0 10px 0;               /* 底部外边距 */
  color: #e65100;                   /* 深橙色文字 */
}

/* 限制信息列表 */
.limit-info ul {
  margin: 0;                        /* 移除默认外边距 */
  padding-left: 20px;               /* 左侧内边距（缩进） */
  color: #666;                      /* 中灰文字 */
}

/* 列表项 */
.limit-info li {
  margin-bottom: 5px;               /* 底部外边距 */
  line-height: 1.6;                 /* 行高 */
}

/* ==================== 星空主题下弹窗样式优化 ==================== */
/* 分析区块标题：白色文字 */
.theme-starry .analysis-section h4 {
  color: #fff;                      /* 白色文字 */
}

/* 限制信息框：半透明橙色背景 */
.theme-starry .limit-info {
  background: rgba(255, 152, 0, 0.2);  /* 20%透明橙色 */
  border-left-color: #ff9800;       /* 橙色边框 */
}

/* 限制信息段落：浅橙色文字 */
.theme-starry .limit-info p {
  color: #ffcc80;                   /* 浅橙色 */
}

/* 限制信息列表项：半透明白色 */
.theme-starry .limit-info li {
  color: rgba(255, 255, 255, 0.9);  /* 90%透明白色 */
}

/* ==================== 星空主题下粉丝画像样式优化 ==================== */
/* 粉丝信息项：半透明背景 */
.theme-starry .fans-item {
  background: rgba(255, 255, 255, 0.1);  /* 10%透明白色 */
  border-left-color: #64b5f6;       /* 浅蓝色边框 */
}

/* 粉丝信息标题：白色 */
.theme-starry .fans-item-header {
  color: #fff;                      /* 白色文字 */
}

/* 粉丝信息内容：半透明白色 */
.theme-starry .fans-item-content {
  color: rgba(255, 255, 255, 0.8);  /* 80%透明白色 */
}

/* ==================== 星空主题下图片统计数据优化 ==================== */
/* 图片统计容器：半透明背景 */
.theme-starry .image-stats {
  background: rgba(255, 255, 255, 0.1);  /* 10%透明白色 */
}

/* 统计项文字：半透明白色 */
.theme-starry .stat-item {
  color: rgba(255, 255, 255, 0.9);  /* 90%透明白色 */
}

/* 统计项图标：更低透明度 */
.theme-starry .stat-item .ivu-icon {
  color: rgba(255, 255, 255, 0.7);  /* 70%透明白色 */
}

/* ==================== 详情弹窗样式系统 ==================== */
/* 弹窗内容容器 */
.detail-content {
  padding: 10px;                    /* 内边距 */
}

/* 详情区块：每个信息部分 */
.detail-section {
  margin-bottom: 25px;              /* 底部外边距 */
  border-bottom: 1px solid #eee;   /* 底部分隔线 */
  padding-bottom: 20px;             /* 底部内边距 */
}

/* 最后一个区块：移除分隔线 */
.detail-section:last-child {
  border-bottom: none;
}

/* 区块标题：左侧蓝色边框 */
.section-title {
  font-size: 16px;                  /* 字体大小 */
  font-weight: 600;                 /* 加粗 */
  color: #333;                      /* 深灰文字 */
  margin: 0 0 15px 0;               /* 上右下左外边距 */
  padding-left: 10px;               /* 左侧内边距 */
  border-left: 4px solid #1890ff;   /* 左侧蓝色边框 */
}

/* ==================== 信息网格（2列布局）==================== */
.info-grid {
  display: grid;                    /* Grid网格布局 */
  grid-template-columns: repeat(2, 1fr);  /* 2列等宽 */
  gap: 15px;                        /* 网格间距 */
}

/* 单个信息项 */
.info-item {
  display: flex;                    /* Flexbox布局 */
  align-items: center;              /* 垂直居中 */
  padding: 8px 12px;                /* 内边距 */
  background: #f8f9fa;              /* 浅灰背景 */
  border-radius: 4px;               /* 小圆角 */
}

/* 全宽信息项：跨越所有列 */
.info-item.full-width {
  grid-column: 1 / -1;              /* 从第1列到最后一列 */
}

/* 信息标签：说明文字 */
.info-label {
  font-weight: 500;                 /* 中等字重 */
  color: #666;                      /* 中灰文字 */
  min-width: 80px;                  /* 最小宽度 */
}

/* 信息值：实际数据 */
.info-value {
  color: #333;                      /* 深灰文字 */
  flex: 1;                          /* 占据剩余空间 */
  word-break: break-all;            /* 允许单词内换行 */
}

/* sec_uid特殊样式：等宽字体 */
.info-value.sec-uid {
  font-family: monospace;           /* 等宽字体 */
  font-size: 12px;                  /* 小字体 */
  color: #999;                      /* 浅灰文字 */
}

/* ==================== 指标网格（3列布局）==================== */
.metrics-grid {
  display: grid;                    /* Grid网格布局 */
  grid-template-columns: repeat(3, 1fr);  /* 3列等宽 */
  gap: 15px;                        /* 网格间距 */
}

/* 单个指标项：紫色渐变背景 */
.metric-item {
  text-align: center;               /* 文字居中 */
  padding: 20px;                    /* 内边距 */
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);  /* 紫色渐变 */
  border-radius: 8px;               /* 圆角 */
  color: #fff;                      /* 白色文字 */
}

/* 指标数值：大字体加粗 */
.metric-item .metric-value {
  font-size: 32px;                  /* 超大字体 */
  font-weight: bold;                /* 加粗 */
  margin-bottom: 8px;               /* 底部外边距 */
}

/* 指标标签：说明文字 */
.metric-item .metric-label {
  font-size: 14px;                  /* 字体大小 */
  opacity: 0.9;                     /* 轻微透明 */
}

/* 详情表格容器 */
.detail-table {
  margin-top: 15px;                 /* 顶部外边距 */
}

/* sec_uid文本：截断显示 */
.sec-uid-text {
  font-size: 12px;                  /* 小字体 */
  color: #666;                      /* 中灰文字 */
  display: inline-block;            /* 行内块元素 */
  max-width: 100px;                 /* 最大宽度 */
  overflow: hidden;                 /* 隐藏溢出 */
  text-overflow: ellipsis;          /* 省略号 */
  white-space: nowrap;              /* 不换行 */
}

/* ==================== 全局操作按钮组样式 ==================== */
/* 操作按钮组：水平排列，居中对齐 */
.action-btn-group {
  display: flex;                    /* Flexbox布局 */
  gap: 8px;                         /* 按钮间距 */
  align-items: center;              /* 垂直居中 */
  justify-content: center;          /* 水平居中 */
}

/* 按钮外边距重置 */
.action-btn-group .ivu-btn {
  margin: 0 !important;             /* 移除默认外边距 */
}

/* ==================== 详情弹窗图片网格样式 ==================== */
/* 图片网格：自适应列，最小120px */
.detail-image-grid {
  display: grid;                    /* Grid网格布局 */
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));  /* 自适应列 */
  gap: 10px;                        /* 网格间距 */
  padding: 10px;                    /* 内边距 */
  background: #f8f9fa;              /* 浅灰背景 */
  border-radius: 8px;               /* 圆角 */
}

/* 单个图片项 */
.detail-image-item {
  text-align: center;               /* 文字居中 */
}

/* 网格图片样式 */
.detail-grid-image {
  width: 100%;                      /* 全宽 */
  height: 100px;                    /* 固定高度 */
  object-fit: cover;                /* 裁剪填充 */
  border-radius: 4px;               /* 小圆角 */
  margin-bottom: 5px;               /* 底部外边距 */
  transition: transform 0.3s;       /* 平滑过渡 */
}

/* 图片悬浮效果：放大 */
.detail-grid-image:hover {
  transform: scale(1.05);           /* 放大5% */
}

/* 图片名称文字 */
.detail-image-name {
  font-size: 12px;                  /* 小字体 */
  color: #666;                      /* 中灰文字 */
  margin: 0;                        /* 移除默认外边距 */
  line-height: 1.4;                 /* 行高 */
  font-weight: 500;                 /* 中等字重 */
}

/* ==================== 详情图片统计数据 ==================== */
/* 统计信息容器：白色背景，带阴影 */
.detail-image-stats {
  display: flex;                    /* Flexbox布局 */
  justify-content: space-around;    /* 均匀分布 */
  align-items: center;              /* 垂直居中 */
  padding: 6px 4px;                 /* 内边距 */
  background: #fff;                 /* 白色背景 */
  border-radius: 4px;               /* 小圆角 */
  margin-top: 5px;                  /* 顶部外边距 */
  font-size: 11px;                  /* 超小字体 */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);  /* 轻微阴影 */
}

/* 单个统计项 */
.detail-stat-item {
  display: flex;                    /* Flexbox布局 */
  align-items: center;              /* 垂直居中 */
  gap: 3px;                         /* 图标和数字间距 */
  color: #666;                      /* 中灰文字 */
  cursor: default;                  /* 默认鼠标指针 */
}

/* 统计项图标 */
.detail-stat-item .ivu-icon {
  color: #999;                      /* 浅灰图标 */
}

/* ==================== 星空主题下详情弹窗图片网格优化 ==================== */
/* 图片网格容器：半透明背景 */
.theme-starry .detail-image-grid {
  background: rgba(255, 255, 255, 0.1);  /* 10%透明白色 */
}

/* 图片名称：半透明白色 */
.theme-starry .detail-image-name {
  color: rgba(255, 255, 255, 0.9);  /* 90%透明白色 */
}

/* 图片统计容器：半透明背景，较深阴影 */
.theme-starry .detail-image-stats {
  background: rgba(255, 255, 255, 0.1);  /* 10%透明白色 */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);  /* 较深阴影 */
}

/* 统计项文字：半透明白色 */
.theme-starry .detail-stat-item {
  color: rgba(255, 255, 255, 0.9);  /* 90%透明白色 */
}

/* 统计项图标：更低透明度 */
.theme-starry .detail-stat-item .ivu-icon {
  color: rgba(255, 255, 255, 0.7);  /* 70%透明白色 */
}
</style>

<!-- ==================== 非 scoped 全局样式 ==================== -->
<!-- 注意：这部分样式没有scoped属性，会全局生效 -->
<!-- 主要用于穿透View UI Plus Table组件的样式 -->
<style>
/* ========================================
   星空主题下查询历史表格样式（全局）
   ======================================== */
/* 历史记录容器：半透明背景 + 毛玻璃效果 */
.theme-starry .history-section {
  background: rgba(255, 255, 255, 0.1) !important;  /* 10%透明白色 */
  backdrop-filter: blur(10px);      /* 毛玻璃模糊 */
  border: 1px solid rgba(255, 255, 255, 0.2);  /* 半透明白色边框 */
  border-radius: 8px;               /* 圆角 */
  padding: 20px;                    /* 内边距 */
  margin-top: 20px;                 /* 顶部外边距 */
}

/* 历史记录标题：白色文字 */
.theme-starry .history-section .history-header h3 {
  color: #fff;                      /* 白色文字 */
}

/* ==================== 查询历史表格样式（穿透View UI Plus组件）==================== */
/* 表格容器：透明背景 */
.theme-starry .history-section .ivu-table-wrapper {
  background: transparent !important;  /* 透明背景 */
  border: none !important;          /* 移除边框 */
}

/* 表格整体和table元素：透明背景，白色文字 */
.theme-starry .history-section .ivu-table,
.theme-starry .history-section .ivu-table table {
  color: #fff !important;           /* 白色文字 */
  background: transparent !important;  /* 透明背景 */
}

/* 表头单元格：深色半透明背景，白色文字 */
.theme-starry .history-section .ivu-table th,
.theme-starry .history-section .ivu-table thead th,
.theme-starry .history-section .ivu-table-header th {
  color: #fff !important;           /* 白色文字 */
  background: rgba(0, 0, 0, 0.4) !important;  /* 40%透明黑色背景 */
  border-color: rgba(255, 255, 255, 0.15) !important;  /* 15%透明白色边框 */
}

.theme-starry .history-section .ivu-table td,
.theme-starry .history-section .ivu-table tbody td,
.theme-starry .history-section .ivu-table-body td,
.theme-starry .history-section .ivu-table-cell {
  color: #fff !important;
  background: transparent !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* 查询历史表格行背景 */
.theme-starry .history-section .ivu-table tr,
.theme-starry .history-section .ivu-table tbody tr,
.theme-starry .history-section .ivu-table-body tr,
.theme-starry .history-section .ivu-table-body table tr {
  background: transparent !important;
}

/* 查询历史表格边框 */
.theme-starry .history-section .ivu-table::before,
.theme-starry .history-section .ivu-table::after,
.theme-starry .history-section .ivu-table td::before,
.theme-starry .history-section .ivu-table td::after,
.theme-starry .history-section .ivu-table th::before,
.theme-starry .history-section .ivu-table th::after {
  background: rgba(255, 255, 255, 0.15) !important;
}

/* 查询历史表格斑马线行样式 */
.theme-starry .history-section .ivu-table-body tr:nth-child(2n) td {
  background: rgba(0, 0, 0, 0.15) !important;
  background-color: rgba(0, 0, 0, 0.15) !important;
}

.theme-starry .history-section .ivu-table-body tr:nth-child(2n+1) td,
.theme-starry .history-section .ivu-table-body tr:nth-child(2n+1) td > div {
  background: transparent !important;
  background-color: transparent !important;
}

/* 查询历史表格悬浮行背景 */
.theme-starry .history-section .ivu-table-body tr.ivu-table-row-hover td {
  background: rgba(255, 255, 255, 0.1) !important;
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.theme-starry .history-section .ivu-table-body tr:nth-child(2n).ivu-table-row-hover td {
  background: rgba(255, 255, 255, 0.08) !important;
  background-color: rgba(255, 255, 255, 0.08) !important;
}

/* 覆盖 table 元素本身的背景 */
.theme-starry .history-section .ivu-table table,
.theme-starry .history-section .ivu-table table tbody,
.theme-starry .history-section .ivu-table table tbody tr,
.theme-starry .history-section .ivu-table table tbody tr td {
  background: transparent !important;
  background-color: transparent !important;
}

/* 确保没有任何白色背景 */
.theme-starry .history-section .ivu-table-body td,
.theme-starry .history-section .ivu-table-body tr td {
  background: transparent !important;
  background-color: transparent !important;
}

/* 但保留偶数行的深蓝色背景 */
.theme-starry .history-section .ivu-table-body tr:nth-child(2n) td {
  background: rgba(0, 0, 0, 0.15) !important;
  background-color: rgba(0, 0, 0, 0.15) !important;
}

/* 查询历史空记录提示 */
.theme-starry .history-section .empty-history {
  color: rgba(255, 255, 255, 0.6);
}

/* 查询历史分页文字颜色 */
.theme-starry .history-section .page-info {
  color: rgba(255, 255, 255, 0.9) !important;
}

/* 查询历史操作按钮样式 */
.theme-starry .history-section .action-btn-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* ========================================
   全局星空主题样式 - 覆盖所有可能的白色背景
   ======================================== */
.theme-starry .ivu-table {
  background: transparent !important;
}

.theme-starry .ivu-table-wrapper {
  background: transparent !important;
}

.theme-starry .ivu-table th {
  background: rgba(0, 0, 0, 0.4) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-shadow: 0 0 3px rgba(255, 255, 255, 0.5);
}

.theme-starry .ivu-table th * {
  color: #fff !important;
  font-weight: 700 !important;
}

.theme-starry .ivu-table td {
  background: transparent !important;
  color: #fff !important;
  font-weight: 600 !important;
  text-shadow: 0 0 2px rgba(255, 255, 255, 0.3);
}

.theme-starry .ivu-table td * {
  color: #fff !important;
}

.theme-starry .ivu-table-stripe .ivu-table-body tr:nth-child(2n) td {
  background: rgba(0, 0, 0, 0.15) !important;
}

/* 星空主题下所有文字颜色 */
.theme-starry .ivu-table td span,
.theme-starry .ivu-table td div,
.theme-starry .ivu-table td p {
  color: #fff !important;
}

/* 星空主题下标签样式优化 */
.theme-starry .ivu-tag {
  border: 1px solid rgba(255, 255, 255, 0.6) !important;
  background: rgba(0, 0, 0, 0.4) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-shadow: 0 0 3px rgba(255, 255, 255, 0.8);
  padding: 4px 12px !important;
}

/* 星空主题下标签内部文字 */
.theme-starry .ivu-tag span {
  color: #fff !important;
  font-weight: 700 !important;
  text-shadow: 0 0 3px rgba(255, 255, 255, 0.8);
}

/* 星空主题下分数文字颜色 */
.theme-starry .ivu-table td .score-text {
  color: #fff !important;
  font-weight: 700 !important;
  text-shadow: 0 0 3px rgba(255, 255, 255, 0.6);
  font-size: 15px !important;
}

/* 星空主题下时间文字 */
.theme-starry .ivu-table td .time-text {
  color: #fff !important;
  font-weight: 600 !important;
  text-shadow: 0 0 2px rgba(255, 255, 255, 0.4);
}

/* 星空主题下昵称文字 */
.theme-starry .ivu-table td .nickname-text {
  color: #fff !important;
  font-weight: 600 !important;
  text-shadow: 0 0 2px rgba(255, 255, 255, 0.4);
}

/* 星空主题下所有表格文字增强 */
.theme-starry .ivu-table td,
.theme-starry .ivu-table td * {
  color: #fff !important;
  text-shadow: 0 0 2px rgba(255, 255, 255, 0.3);
}

/* 星空主题下表格头部文字增强 */
.theme-starry .ivu-table th,
.theme-starry .ivu-table th * {
  color: #fff !important;
  font-weight: 700 !important;
  text-shadow: 0 0 3px rgba(255, 255, 255, 0.5);
}

/* 星空主题下操作按钮组 */
.theme-starry .action-btn-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* 星空主题下分页信息 */
.theme-starry .page-info,
.theme-starry .history-pagination .page-info {
  color: rgba(255, 255, 255, 0.9) !important;
}

/* 星空主题下空记录提示 */
.theme-starry .empty-history {
  color: rgba(255, 255, 255, 0.6) !important;
}

/* 星空主题下所有文本元素 */
.theme-starry * {
  color: inherit;
}

/* 确保特定元素有正确的颜色 */
.theme-starry .ivu-table td,
.theme-starry .ivu-table th,
.theme-starry .ivu-table-body td,
.theme-starry .ivu-table-cell {
  color: #fff !important;
}

/* ========================================
   经典主题下查询历史表格样式优化
   ======================================== */
.theme-default .history-section .ivu-table {
  border: 2px solid #b0b0b0;
}

.theme-default .history-section .ivu-table th,
.theme-default .history-section .ivu-table thead th,
.theme-default .history-section .ivu-table-header th {
  background: #e0e0e0 !important;
  font-weight: 700;
  color: #000000 !important;
  border-color: #b0b0b0 !important;
}

.theme-default .history-section .ivu-table th *,
.theme-default .history-section .ivu-table thead th *,
.theme-default .history-section .ivu-table-header th * {
  color: #000000 !important;
}

.theme-default .history-section .ivu-table td,
.theme-default .history-section .ivu-table tbody td,
.theme-default .history-section .ivu-table-body td,
.theme-default .history-section .ivu-table-cell {
  color: #000000 !important;
  border-color: #b0b0b0 !important;
}

.theme-default .history-section .ivu-table td *,
.theme-default .history-section .ivu-table tbody td *,
.theme-default .history-section .ivu-table-body td *,
.theme-default .history-section .ivu-table-cell * {
  color: #000000 !important;
}

.theme-default .history-section .ivu-table-body tr:nth-child(2n) td {
  background: #f0f0f0 !important;
}

.theme-default .history-section .ivu-table-body tr.ivu-table-row-hover td {
  background: #d6e4ff !important;
}

.theme-default .history-section .ivu-table-body tr:nth-child(2n).ivu-table-row-hover td {
  background: #adc6ff !important;
}

/* 经典模式下标签文字 */
.theme-default .ivu-tag,
.theme-default .ivu-tag span {
  color: #000000 !important;
}

/* 经典模式下分数文字 */
.theme-default .score-text {
  color: #000000 !important;
  font-weight: 700 !important;
}

/* 经典模式下时间文字 */
.theme-default .time-text {
  color: #000000 !important;
}

/* 经典模式下昵称文字 */
.theme-default .nickname-text {
  color: #000000 !important;
}

/* 经典模式下分页信息 */
.theme-default .page-info {
  color: #000000 !important;
}

/* 经典模式下空记录提示 */
.theme-default .empty-history {
  color: #666666 !important;
}

/* 经典模式下操作按钮文字 */
.theme-default .action-btn-group .ivu-btn,
.theme-default .action-btn-group .ivu-btn span {
  color: #000000 !important;
}

/* ========================================
   经典主题下详细数据表格样式优化
   ======================================== */
.theme-default .weight-result .data-table-section .ivu-table th,
.theme-default .weight-result .data-table-section .ivu-table thead th,
.theme-default .weight-result .data-table-section .ivu-table-header th {
  background: #e0e0e0 !important;
  color: #000000 !important;
  font-weight: 700;
  border-color: #b0b0b0 !important;
}

.theme-default .weight-result .data-table-section .ivu-table th *,
.theme-default .weight-result .data-table-section .ivu-table thead th *,
.theme-default .weight-result .data-table-section .ivu-table-header th * {
  color: #000000 !important;
}

.theme-default .weight-result .data-table-section .ivu-table td,
.theme-default .weight-result .data-table-section .ivu-table tbody td,
.theme-default .weight-result .data-table-section .ivu-table-body td,
.theme-default .weight-result .data-table-section .ivu-table-cell {
  color: #000000 !important;
  border-color: #b0b0b0 !important;
}

.theme-default .weight-result .data-table-section .ivu-table td *,
.theme-default .weight-result .data-table-section .ivu-table tbody td *,
.theme-default .weight-result .data-table-section .ivu-table-body td *,
.theme-default .weight-result .data-table-section .ivu-table-cell * {
  color: #000000 !important;
}

.theme-default .weight-result .data-table-section .ivu-table-body tr:nth-child(2n) td {
  background: #f0f0f0 !important;
}

/* 经典模式下全局文字颜色强制覆盖 */
.theme-default .table-header h3,
.theme-default .history-header h3 {
  color: #000000 !important;
  font-weight: 700;
}

.theme-default .user-details h3 {
  color: #000000 !important;
}

.theme-default .account-id {
  color: #333333 !important;
}

.theme-default .description {
  color: #666666 !important;
}

.theme-default .vip-info,
.theme-default .vip-info span {
  color: #ff4d4f !important;
}

.theme-default .points-info,
.theme-default .points-info span {
  color: #000000 !important;
}

.theme-default .query-cost,
.theme-default .query-cost span {
  color: #333333 !important;
}

.theme-default .metric-value {
  color: #000000 !important;
  font-weight: 700;
}

.theme-default .metric-label {
  color: #333333 !important;
}

/* 经典模式下核心指标卡片样式 */
.theme-default .metric-card {
  background: linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 2px solid #b0b0b0;
}

/* ==================== AI 账号权重分析样式 ==================== */

/* AI 分析入口区域容器 */
.ai-analyze-section {
  /* 外边距：与上下内容保持间距 */
  margin: 20px 0;
  /* 内边距 */
  padding: 20px;
  /* 半透明深色背景，与页面主题协调 */
  background: rgba(255, 255, 255, 0.05);
  /* 圆角 */
  border-radius: 12px;
  /* 左边框高亮 */
  border-left: 4px solid #ff9900;
}

/* AI 分析按钮 */
.ai-analyze-btn {
  /* 加粗文字 */
  font-weight: 600;
  /* 大字号 */
  font-size: 16px;
  /* 按钮高度 */
  height: 44px;
}

/* AI 分析按钮内的图标 */
.ai-analyze-btn i {
  /* 图标与文字间距 */
  margin-right: 8px;
  /* 图标字号 */
  font-size: 18px;
}

/* AI 分析中按钮内文字：强制深色加粗，避免在 loading 状态下变得发灰 */
.ai-analyze-btn .ai-btn-loading {
  /* 强制深色文字（与 warning 按钮橙色背景形成高对比） */
  color: #5c3300;
  /* 加粗 */
  font-weight: 700;
  /* 与火焰图标间距 */
  margin-left: 4px;
}

/* AI 分析进度条 */
.ai-analyze-progress {
  /* 进度条顶部间距 */
  margin-top: 15px;
}

/* AI 分析中加载文字：深色加粗醒目，与背景对比度高 */
.ai-analyze-loading-text {
  /* 顶部间距 */
  margin-top: 12px;
  /* 文字居中 */
  text-align: center;
  /* 深橙色文字（与 warning 按钮同色系，深色背景下醒目） */
  color: #ff9900;
  /* 加粗 */
  font-weight: 600;
  /* 稍大字号 */
  font-size: 14px;
  /* 文字阴影增强可读性 */
  text-shadow: 0 0 4px rgba(255, 153, 0, 0.3);
}

/* AI 分析中加载文字图标 */
.ai-analyze-loading-text i {
  /* 图标与文字间距 */
  margin-right: 6px;
  /* 图标动画（轻微闪烁） */
  animation: ai-loading-blink 1.2s ease-in-out infinite;
}

/* AI 分析中加载文字的闪烁动画 */
@keyframes ai-loading-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* AI 分析提示文字 */
.ai-analyze-tip {
  /* 顶部间距 */
  margin-top: 10px;
  /* 文字居中 */
  text-align: center;
  /* 浅灰色文字 */
  color: #999;
  /* 小字号 */
  font-size: 12px;
}

/* AI 分析结果区域容器 */
.ai-result-section {
  /* 顶部间距 */
  margin-top: 25px;
  /* 内边距 */
  padding: 20px;
  /* 半透明背景 */
  background: rgba(0, 0, 0, 0.2);
  /* 圆角 */
  border-radius: 12px;
  /* 左边框高亮 */
  border-left: 4px solid #19be6b;
}

/* AI 分析结果区域标题栏 */
.ai-result-header {
  /* Flex 布局 */
  display: flex;
  /* 两端对齐 */
  justify-content: space-between;
  /* 垂直居中 */
  align-items: center;
  /* 底部间距 */
  margin-bottom: 15px;
}

/* AI 分析结果区域标题 */
.ai-result-header h3 {
  /* 去除默认 margin */
  margin: 0;
  /* 白色文字 */
  color: #fff;
  /* 字号 */
  font-size: 18px;
}

/* AI 分析结果卡片 */
.ai-result-card {
  /* 顶部间距 */
  margin-top: 15px;
  /* 内边距 */
  padding: 15px;
  /* 背景色 */
  background: rgba(255, 255, 255, 0.08);
  /* 圆角 */
  border-radius: 8px;
}

/* AI 分析结果卡片标题 */
.ai-result-title {
  /* 底部间距 */
  margin-bottom: 10px;
  /* 字号 */
  font-size: 15px;
  /* 字体加粗 */
  font-weight: 600;
  /* 浅黄色标题，突出显示 */
  color: #ffcc00;
}

/* AI 分析结果内容 */
.ai-result-content {
  /* 行高 */
  line-height: 1.8;
  /* 浅灰色文字，便于阅读长文本 */
  color: #e0e0e0;
  /* 字号 */
  font-size: 14px;
  /* 保留空白字符和换行 */
  white-space: pre-wrap;
  /* 自动换行 */
  word-wrap: break-word;
}

/* 经典主题下的 AI 区域样式覆盖 */
.theme-default .ai-analyze-section {
  background: rgba(0, 0, 0, 0.03);
  border-left-color: #ff9900;
}

.theme-default .ai-result-section {
  background: rgba(0, 0, 0, 0.05);
  border-left-color: #19be6b;
}

.theme-default .ai-result-header h3 {
  color: #000;
}

.theme-default .ai-result-card {
  background: rgba(255, 255, 255, 0.9);
}

.theme-default .ai-result-title {
  color: #ff9900;
}

.theme-default .ai-result-content {
  color: #333;
}

.theme-default .ai-analyze-tip {
  color: #666;
}
</style>